import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Download,
  ExternalLink,
  Eye,
  History,
  ListPlus,
  Lock,
  Loader,
  RefreshCcw,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';
import { buildDisplayedSectionChangeSummary } from '../../utils/brdChangeSummary';

const buildSectionTree = (sections = []) => {
  const nodesBySectionNumber = {};
  const roots = [];

  sections.forEach((section) => {
    const sectionNumber = String(section?.section_number || '').trim();
    if (!sectionNumber) return;
    nodesBySectionNumber[sectionNumber] = {
      ...section,
      children: [],
    };
  });

  sections.forEach((section) => {
    const sectionNumber = String(section?.section_number || '').trim();
    const node = nodesBySectionNumber[sectionNumber];
    if (!node) return;

    const isFlattenedGenerationSection = Boolean(section?.grouping_method);
    const inferredParentSectionNumber =
      !isFlattenedGenerationSection && sectionNumber.includes('.')
        ? sectionNumber.split('.').slice(0, -1).join('.')
        : '';
    const parentSectionNumber = String(section?.parent_section_number || inferredParentSectionNumber).trim();
    const parent = nodesBySectionNumber[parentSectionNumber];
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
};

const mapSectionNodesByNumber = (nodes = [], output = new Map()) => {
  nodes.forEach((node) => {
    const sectionNumber = Number(node?.number);
    if (Number.isFinite(sectionNumber)) {
      output.set(sectionNumber, node);
    }
    if (Array.isArray(node.children) && node.children.length > 0) {
      mapSectionNodesByNumber(node.children, output);
    }
  });
  return output;
};

const collectSectionDescendants = (section = null, output = []) => {
  if (!section || !Array.isArray(section.children)) return output;
  section.children.forEach((child) => {
    output.push(child);
    collectSectionDescendants(child, output);
  });
  return output;
};

const normalizeIdentityText = (value = '') =>
  String(value || '')
    .replace(/\u00a0/g, ' ')
    .trim()
    .replace(/^(?:section|chapter|part)\s+\d+(?:\.\d+)*\.?\s*[-:)]?\s+/i, '')
    .replace(/^\d+(?:\.\d+)*\.?\s*[-:)]?\s+/, '')
    .replace(/^\([a-z]\)\s+/i, '')
    .replace(/^[a-z]\)\s+/i, '')
    .replace(/[^a-z0-9]+/gi, ' ')
    .trim()
    .toLowerCase();

const normalizeIdentityPath = (segments = []) =>
  (Array.isArray(segments) ? segments : [])
    .map((segment) => normalizeIdentityText(segment))
    .filter(Boolean)
    .join(' > ');

const normalizeIdentityKey = (value = '') =>
  String(value || '')
    .split('>')
    .map((segment) => normalizeIdentityText(segment))
    .filter(Boolean)
    .join(' > ');

const normalizeChangeType = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'deleted' ? 'removed' : normalized;
};

const getChangeTypeLabel = (value = '') => {
  const normalized = normalizeChangeType(value);
  if (normalized === 'added') return 'Added';
  if (normalized === 'removed') return 'Removed';
  if (normalized === 'modified') return 'Modified';
  return 'Changed';
};

const getChangeTypeClass = (value = '') => {
  const normalized = normalizeChangeType(value);
  if (normalized === 'added') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (normalized === 'removed') return 'border-rose-200 bg-rose-50 text-rose-700';
  if (normalized === 'modified') return 'border-blue-200 bg-blue-50 text-blue-700';
  return 'border-slate-200 bg-slate-50 text-slate-700';
};

const getChangeRequirementSection = (change = {}) =>
  change?.new_section || change?.old_section || {};

const stripLeadingSectionNumber = (title = '', sectionNumber = '') => {
  const cleanTitle = String(title || '').trim();
  const cleanNumber = String(sectionNumber || '').trim();
  if (!cleanTitle || !cleanNumber) return cleanTitle;
  const escapedNumber = cleanNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return cleanTitle.replace(new RegExp(`^${escapedNumber}\\s*[-.:)]?\\s*`, 'i'), '').trim() || cleanTitle;
};

const getRequirementLabel = (section = {}) => {
  const number = String(section?.section_number || '').trim();
  const title = stripLeadingSectionNumber(String(section?.title || section?.path || '').trim(), number);
  return [number, title].filter(Boolean).join(' · ') || 'Requirement section';
};

const formatGeneratedSectionLabel = (section = {}) => {
  const number = Number(section?.number || 0);
  const title = String(section?.title || section?.section_title || '').trim();
  const brdNumber = String(section?.section_number || '').trim();
  const primary = number > 0 ? `Section ${number}` : 'Displayed section';
  const label = [primary, title].filter(Boolean).join(': ');
  return brdNumber ? `${label} · BRD ${brdNumber}` : label;
};

const getUniqueGeneratedSections = (impactItem = null) => {
  const rows = [
    ...(Array.isArray(impactItem?.current_generation_sections) ? impactItem.current_generation_sections : []),
    ...(Array.isArray(impactItem?.previous_generation_sections) ? impactItem.previous_generation_sections : []),
  ];
  const seen = new Set();
  return rows.filter((section) => {
    const key = [
      Number(section?.number || 0),
      String(section?.section_number || '').trim(),
      String(section?.title || section?.section_title || '').trim(),
    ].join('|');
    if (seen.has(key)) return false;
    seen.add(key);
    return Boolean(String(section?.title || section?.section_title || '').trim() || Number(section?.number || 0) > 0);
  });
};

const getSourceChangeLabel = (changeType = 'modified') => {
  const normalized = normalizeChangeType(changeType);
  if (normalized === 'added') return 'Added source subsection';
  if (normalized === 'removed') return 'Removed source subsection';
  return 'Changed source subsection';
};

const getChangeReviewKey = (change = {}, index = 0) => {
  if (change?.change_key) return String(change.change_key);
  const oldSection = change?.old_section || {};
  const newSection = change?.new_section || {};
  const identityKey = (
    newSection?.identity_key
    || oldSection?.identity_key
    || normalizeIdentityKey(newSection?.identity_path || oldSection?.identity_path || newSection?.path || oldSection?.path || newSection?.title || oldSection?.title)
  );
  return [
    normalizeChangeType(change?.change_type || 'change'),
    identityKey,
    index,
  ].join(':');
};

const collectChangeIdentityKeys = (change = {}) => {
  const sections = [change?.old_section, change?.new_section].filter(Boolean);
  const output = [];
  const seen = new Set();
  sections.forEach((section) => {
    const keys = [
      section?.identity_key,
      normalizeIdentityKey(section?.identity_path || ''),
      normalizeIdentityKey(section?.path || ''),
      normalizeIdentityPath([section?.parent_section_title, section?.title]),
      normalizeIdentityText(section?.title),
    ].filter(Boolean);
    keys.forEach((key) => {
      if (!key || seen.has(key)) return;
      seen.add(key);
      output.push(key);
    });
  });
  return output;
};

const getIncludedSourceSections = (section = null) => {
  const explicitSources = Array.isArray(section?.included_source_sections)
    ? section.included_source_sections
    : [];
  const normalizedExplicitSources = explicitSources
    .map((source, index) => ({
      row_index: source?.row_index ?? section?.included_source_row_indexes?.[index] ?? null,
      section_number: String(source?.section_number || '').trim(),
      title: String(source?.title || '').trim(),
    }))
    .filter((source) => source.section_number || source.title);

  if (normalizedExplicitSources.length > 0) {
    return normalizedExplicitSources;
  }

  const numbers = Array.isArray(section?.included_source_section_numbers)
    ? section.included_source_section_numbers
    : [];
  const titles = Array.isArray(section?.included_source_section_titles)
    ? section.included_source_section_titles
    : [];
  return numbers
    .map((number, index) => ({
      row_index: Array.isArray(section?.included_source_row_indexes)
        ? section.included_source_row_indexes[index]
        : null,
      section_number: String(number || '').trim(),
      title: String(titles[index] || '').trim(),
    }))
    .filter((source) => source.section_number || source.title);
};

const collectSectionIdentityKeys = (section = {}, parentSegments = []) => {
  const output = [];
  const seen = new Set();
  const addKey = (key) => {
    const normalized = normalizeIdentityKey(key) || normalizeIdentityText(key);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    output.push(normalized);
  };

  addKey(section?.identity_key);
  addKey(section?.identity_path);
  addKey(normalizeIdentityPath([...(Array.isArray(parentSegments) ? parentSegments : []), section?.title]));
  addKey(normalizeIdentityPath([section?.parent_section_title, section?.title]));
  addKey(section?.title);

  (Array.isArray(section?.included_source_section_titles) ? section.included_source_section_titles : [])
    .forEach(addKey);
  (Array.isArray(section?.included_source_sections) ? section.included_source_sections : [])
    .forEach((source) => {
      addKey(source?.identity_key);
      addKey(source?.identity_path);
      addKey(source?.path);
      addKey(source?.title);
    });
  return output;
};

const getImageCountLabel = (section = {}) => {
  const relevantCount = Number(section?.image_count || 0);
  const candidateCount = Number(section?.image_candidates_count || 0);
  if (candidateCount > relevantCount) {
    return `${relevantCount} relevant / ${candidateCount}`;
  }
  return String(relevantCount);
};

const normalizeOutlineRow = (row = {}) => {
  const rowId = String(row?.row_id || '').trim();
  if (!rowId) return null;
  return {
    ...row,
    row_id: rowId,
    row_index: Number(row?.row_index || 0),
    section_number: String(row?.section_number || '').trim(),
    title: String(row?.title || '').trim(),
    level: Number(row?.level || 1),
    parent_section_number: String(row?.parent_section_number || '').trim(),
    parent_section_title: String(row?.parent_section_title || '').trim(),
    parent_section: String(row?.parent_section || '').trim(),
    full_path: String(row?.full_path || row?.title || '').trim(),
    page_range: String(row?.page_range || '').trim(),
    preview_page: Number(row?.preview_page || 0),
    preview_heading_top: Number(row?.preview_heading_top || 0),
    preview_heading_bottom: Number(row?.preview_heading_bottom || 0),
    child_count: Number(row?.child_count || 0),
    image_count: Number(row?.image_count || 0),
    table_count: Number(row?.table_count || 0),
  };
};

const collectConsideredOutlineRowIds = (sections = [], sectionGrouping = null) => {
  const considered = new Set();
  const addRowId = (value) => {
    const rowId = String(value || '').trim();
    if (rowId) considered.add(rowId);
  };
  const addRowIds = (values) => {
    if (!Array.isArray(values)) return;
    values.forEach(addRowId);
  };

  (sections || []).forEach((section) => {
    addRowId(section?.source_row_id);
    addRowIds(section?.included_source_row_ids);
  });

  const groupingSections = [];
  if (sectionGrouping && typeof sectionGrouping === 'object') {
    if (Array.isArray(sectionGrouping.generation_sections)) {
      groupingSections.push(...sectionGrouping.generation_sections);
    }
    if (Array.isArray(sectionGrouping.manual_restored_sections)) {
      groupingSections.push(...sectionGrouping.manual_restored_sections);
    }
    if (sectionGrouping.llm_response && Array.isArray(sectionGrouping.llm_response.generation_sections)) {
      groupingSections.push(...sectionGrouping.llm_response.generation_sections);
    }
  }

  groupingSections.forEach((group) => {
    addRowId(group?.source_row_id);
    addRowIds(group?.included_row_ids);
    addRowIds(group?.included_source_row_ids);
  });

  return considered;
};

const buildOutlineCoverage = (rows = [], consideredRowIds = new Set()) => {
  const rowIdBySectionNumber = new Map();
  rows.forEach((row) => {
    if (row?.row_id && row?.section_number) {
      rowIdBySectionNumber.set(String(row.section_number).trim(), row.row_id);
    }
  });

  const childrenById = new Map();
  rows.forEach((row) => {
    const rowId = String(row?.row_id || '').trim();
    const parentSectionNumber = String(row?.parent_section_number || '').trim();
    const parentRowId = parentSectionNumber ? rowIdBySectionNumber.get(parentSectionNumber) : '';
    if (!rowId || !parentRowId) return;
    const currentChildren = childrenById.get(parentRowId) || [];
    currentChildren.push(rowId);
    childrenById.set(parentRowId, currentChildren);
  });

  const descendantCache = new Map();
  const collectDescendantIds = (rowId) => {
    if (descendantCache.has(rowId)) return descendantCache.get(rowId);
    const output = [];
    (childrenById.get(rowId) || []).forEach((childId) => {
      output.push(childId);
      output.push(...collectDescendantIds(childId));
    });
    descendantCache.set(rowId, output);
    return output;
  };

  const coverageByRowId = new Map();
  rows.forEach((row) => {
    const rowId = String(row?.row_id || '').trim();
    if (!rowId) return;
    const descendantIds = collectDescendantIds(rowId);
    const leafDescendantIds = descendantIds.filter((descendantId) =>
      (childrenById.get(descendantId) || []).length === 0
    );
    const coveredDescendantIds = descendantIds.filter((descendantId) => consideredRowIds.has(descendantId));
    const coveredLeafDescendantIds = leafDescendantIds.filter((descendantId) => consideredRowIds.has(descendantId));
    const isSelfConsidered = consideredRowIds.has(rowId);

    coverageByRowId.set(rowId, {
      descendantCount: descendantIds.length,
      leafDescendantCount: leafDescendantIds.length,
      coveredDescendantCount: coveredDescendantIds.length,
      coveredLeafDescendantCount: coveredLeafDescendantIds.length,
      coveredByChildren: !isSelfConsidered && coveredDescendantIds.length > 0,
      fullyCoveredByChildren: (
        !isSelfConsidered
        && leafDescendantIds.length > 0
        && coveredLeafDescendantIds.length === leafDescendantIds.length
      ),
    });
  });

  return coverageByRowId;
};

const filterOutlineRows = (rows = [], query = '') => {
  const normalizedQuery = String(query || '').trim().toLowerCase();
  if (!normalizedQuery) return rows;
  return rows.filter((row) => [
    row.section_number,
    row.title,
    row.parent_section_title,
    row.full_path,
    row.page_range,
    row.page,
  ].some((value) => String(value || '').toLowerCase().includes(normalizedQuery)));
};

const getFirstIndexedPage = (indexedPages = '') => {
  const match = String(indexedPages || '').match(/\d+/);
  if (!match) return 1;
  return Math.max(1, Number(match[0]) || 1);
};

const getPreviewPage = (item = {}, fallbackPages = '') => {
  const explicitPage = Number(item?.preview_page || 0);
  if (Number.isFinite(explicitPage) && explicitPage > 0) {
    return Math.max(1, Math.floor(explicitPage));
  }
  return getFirstIndexedPage(fallbackPages || item?.indexed_pages || item?.page_range);
};

const getPreviewHeadingTop = (item = {}) => {
  const top = Number(item?.preview_heading_top || 0);
  return Number.isFinite(top) && top > 0 ? top : 0;
};

const buildPdfPreviewFragment = (page, headingTop = 0) => {
  const normalizedPage = Math.max(1, Number(page) || 1);
  const top = Number(headingTop || 0);
  const viewParam = Number.isFinite(top) && top > 0
    ? `view=FitH,${Math.max(0, Math.floor(top - 48))}`
    : 'zoom=page-width';
  return `page=${normalizedPage}&${viewParam}&toolbar=0&navpanes=0`;
};

const normalizeEvidenceText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

const getEvidenceExcerpt = (chunk = {}) => {
  const explicitExcerpt = normalizeEvidenceText(chunk.excerpt || chunk.content);
  if (explicitExcerpt) return explicitExcerpt;
  const fullText = normalizeEvidenceText(chunk.text);
  return fullText.length > 260 ? `${fullText.slice(0, 260)}...` : fullText;
};

const getEvidenceFullText = (chunk = {}) => {
  const fullText = String(chunk.text || '')
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .trim();
  return fullText || getEvidenceExcerpt(chunk);
};

const getEvidencePageLabel = (chunk = {}) => {
  const pages = Array.isArray(chunk.pages) && chunk.pages.length > 0
    ? chunk.pages
    : chunk.page
      ? [chunk.page]
      : [];
  if (pages.length === 0) return 'Source page';
  return `${pages.length === 1 ? 'Page' : 'Pages'} ${pages.join(', ')}`;
};

const formatExtractionMethodLabel = (method = '', grouping = null) => {
  const normalized = String(method || '').trim().toLowerCase();
  if (normalized === 'feature list') return 'Feature-based Sections';
  if (normalized === 'feature list fallback') return 'Auto-detected Features';
  if (normalized === 'embedded toc' || normalized === 'toc') return 'PDF Table of Contents';
  if (normalized === 'word document outline' || normalized === 'word_document_outline') return 'Word Document Outline';
  if (normalized === 'rebuilt layout toc' || normalized === 'layout toc') {
    return grouping?.rebuilt_toc_approved
      ? 'Rebuilt Table of Contents'
      : 'Auto-built Table of Contents';
  }
  if (normalized === 'character fallback' || normalized === 'character chunk fallback') return 'Full Document Scan';
  if (normalized === 'manual input') return 'Manual Requirements';
  if (normalized === 'openapi operation extraction') return 'API Contract Operations';
  return String(method || '').trim() || 'Not available';
};

const getExtractionMethodNotice = (method = '', grouping = null) => {
  const normalized = String(method || '').trim().toLowerCase();
  const groupingMethod = String(grouping?.method || '').trim().toLowerCase();
  if ((normalized === 'rebuilt layout toc' || normalized === 'layout toc') && grouping?.rebuilt_toc_approved) {
    const usedOutlineFallback = groupingMethod === 'outline_hierarchy_fallback' || grouping?.llm_timeout_fallback;
    return {
      title: usedOutlineFallback ? 'Outline was rebuilt; structure was used' : 'Outline was rebuilt',
      description: usedOutlineFallback
        ? 'You replaced the original PDF outline with a layout-built outline. AI grouping took too long, so sections were created from that rebuilt outline structure.'
        : 'You replaced the original PDF outline with a new outline built from the document layout. Review the rebuilt sections before generating tests.',
      toneClass: usedOutlineFallback
        ? 'border-amber-200 bg-amber-50 text-amber-900'
        : 'border-sky-200 bg-sky-50 text-sky-900',
      iconClass: usedOutlineFallback ? 'text-amber-700' : 'text-sky-700',
    };
  }
  if (groupingMethod === 'outline_hierarchy_fallback' || grouping?.llm_timeout_fallback) {
    return {
      title: 'Outline structure was used',
      description: 'AI grouping took too long, so sections were created from the document outline structure. Review the section list before generating tests.',
      toneClass: 'border-amber-200 bg-amber-50 text-amber-900',
      iconClass: 'text-amber-700',
    };
  }
  if (normalized === 'rebuilt layout toc' || normalized === 'layout toc') {
    return {
      title: 'Outline was auto-built',
      description: 'The PDF outline was missing or unreliable, so sections were built from the document layout. Review the section list before generating tests.',
      toneClass: 'border-sky-200 bg-sky-50 text-sky-900',
      iconClass: 'text-sky-700',
    };
  }
  if (normalized === 'feature list fallback') {
    return {
      title: 'Features were auto-detected',
      description: 'No usable document outline was found. Because this BRD is short, sections were grouped by detected product features.',
      toneClass: 'border-amber-200 bg-amber-50 text-amber-900',
      iconClass: 'text-amber-700',
    };
  }
  if (normalized === 'character fallback' || normalized === 'character chunk fallback') {
    return {
      title: 'Full document scan was used',
      description: 'No usable document outline was found. The BRD was scanned into broad content sections, so review the section names before generating tests.',
      toneClass: 'border-amber-200 bg-amber-50 text-amber-900',
      iconClass: 'text-amber-700',
    };
  }
  if (normalized === 'openapi operation extraction') {
    return {
      title: 'API operations extracted',
      description: 'Swagger/OpenAPI endpoints are ready for API test-case generation. No document section extraction is required.',
      toneClass: 'border-cyan-200 bg-cyan-50 text-cyan-900',
      iconClass: 'text-cyan-700',
    };
  }
  if (normalized === 'feature list') {
    return {
      title: 'Feature-based sections selected',
      description: 'Sections are grouped by product features instead of document headings.',
      toneClass: 'border-blue-200 bg-blue-50 text-blue-900',
      iconClass: 'text-blue-700',
    };
  }
  return null;
};

// Artifact timestamps are naive UTC ISO strings.
const formatPlanTime = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  const date = new Date(/(Z|[+-]\d{2}:?\d{2})$/.test(text) ? text : `${text}Z`);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};

export const SectionSelectionStep = ({
  sections,
  rawSectionOutline = [],
  sectionGrouping = null,
  brdChangeAnalysis = null,
  brdUpdatePending = false,
  pdfId = '',
  selectedSection,
  selectedSectionArtifact,
  sectionArtifactIndex = {},
  status,
  bulkMode = 'single',
  bulkSelectedSections = [],
  bulkJob = null,
  bulkItems = [],
  bulkLoading = false,
  bulkError = null,
  onSelectSection,
  onBulkModeChange,
  onToggleBulkSection,
  onSelectAllBulkSections,
  onClearBulkSelection,
  onStartBulkGeneration,
  onViewBulkGeneration,
  onRestoreSkippedTocSections,
  onRemoveRestoredTocSections,
  onApplyRebuiltToc,
  onProcess,
  onDownloadExtractedSectionsJson,
  onViewPdf,
  onOpenLatestSaved,
  onOpenSavedPlan,
  onOpenSavedTestCases,
  onRegenerateDeprecatedSection,
  onBrdChangeAnalysisUpdate,
  isOpenApiSource = false,
}) => {
  const [isReplacementModalOpen, setIsReplacementModalOpen] = useState(false);
  const [isBulkReplacementModalOpen, setIsBulkReplacementModalOpen] = useState(false);
  const [isSkippedTocModalOpen, setIsSkippedTocModalOpen] = useState(false);
  const [tocReviewMode, setTocReviewMode] = useState('skipped');
  const [tocSearchQuery, setTocSearchQuery] = useState('');
  const [selectedSkippedTocRowIds, setSelectedSkippedTocRowIds] = useState(() => new Set());
  const [isRestoringSkippedToc, setIsRestoringSkippedToc] = useState(false);
  const [removingRestoredTocRowId, setRemovingRestoredTocRowId] = useState('');
  const [skippedTocError, setSkippedTocError] = useState('');
  const [isRebuildingToc, setIsRebuildingToc] = useState(false);
  const [versionPickerSection, setVersionPickerSection] = useState(null);
  const [deprecatedPromptSection, setDeprecatedPromptSection] = useState(null);
  const [rebuiltTocError, setRebuiltTocError] = useState('');
  const [isDeltaReviewExpanded, setIsDeltaReviewExpanded] = useState(false);
  const [outlinePreviewRowId, setOutlinePreviewRowId] = useState('');
  const [isOutlinePreviewLoading, setIsOutlinePreviewLoading] = useState(false);
  const [expandedSectionNumbers, setExpandedSectionNumbers] = useState(null);
  const [isPdfPreviewLoading, setIsPdfPreviewLoading] = useState(false);
  const [tocStructureContext, setTocStructureContext] = useState(null);
  const [tocStructureModalOpen, setTocStructureModalOpen] = useState(false);
  const [tocStructureApproved, setTocStructureApproved] = useState(false);
  const [tocStructureLoading, setTocStructureLoading] = useState(false);
  const [tocStructureApplying, setTocStructureApplying] = useState(false);
  const [tocStructureError, setTocStructureError] = useState('');
  const [tocStructureAutoOpenKey, setTocStructureAutoOpenKey] = useState('');
  const isBulkSelectionMode = bulkMode === 'bulk';
  const normalizedBulkStatus = String(bulkJob?.status || '').trim().toLowerCase();
  const bulkJobMode = String(bulkJob?.mode || '').trim().toLowerCase();
  const bulkJobFinished = ['completed', 'failed', 'cancelled', 'partial_completed'].includes(normalizedBulkStatus);
  const rawBusy = status === 'extracting' || bulkLoading
    || ((status === 'processing' || status === 'bulk_processing') && !bulkJobFinished);
  const formatProgressIssue = (message, fallback = 'Something went wrong. Please try again.') => {
    const text = String(message || '').trim();
    if (!text) return fallback;
    if (/\b(queue|queued|worker|bulk)\b/i.test(text)) return fallback;
    return text;
  };
  const busy = rawBusy;
  const bulkSelectedCount = Array.isArray(bulkSelectedSections) ? bulkSelectedSections.length : 0;
  const userFacingBulkError = formatProgressIssue(bulkError, 'Generation could not continue. Please try again.');
  const hasBulkGenerationJob = Boolean(bulkJob) && !bulkJobMode.startsWith('pre-analysis');
  const bulkGenerationActive =
    hasBulkGenerationJob && !bulkJobFinished;
  const shouldShowBulkGenerationAction =
    isBulkSelectionMode && hasBulkGenerationJob && (bulkGenerationActive || bulkSelectedCount === 0);
  const tocMutationBusy = isRestoringSkippedToc
    || Boolean(removingRestoredTocRowId)
    || isRebuildingToc;
  const sectionTree = useMemo(() => buildSectionTree(sections), [sections]);
  const resolvedExpandedSectionNumbers = useMemo(
    () => expandedSectionNumbers || new Set(),
    [expandedSectionNumbers]
  );
  const sectionLookupByNumber = useMemo(
    () => mapSectionNodesByNumber(sectionTree),
    [sectionTree]
  );
  const selectedSectionData = sectionLookupByNumber.get(Number(selectedSection)) || null;
  const bulkSelectedSectionData = useMemo(
    () => (Array.isArray(bulkSelectedSections) ? bulkSelectedSections : [])
      .map((sectionNumber) => sectionLookupByNumber.get(Number(sectionNumber)))
      .filter(Boolean),
    [bulkSelectedSections, sectionLookupByNumber]
  );
  const selectedSectionChildCount = Array.isArray(selectedSectionData?.children)
    ? selectedSectionData.children.length
    : 0;
  const selectedSectionDescendants = useMemo(
    () => collectSectionDescendants(selectedSectionData, []),
    [selectedSectionData]
  );
  const selectedIncludedSourceSections = useMemo(
    () => getIncludedSourceSections(selectedSectionData),
    [selectedSectionData]
  );
  const selectedIncludedSourceCount = Math.max(
    Number(selectedSectionData?.included_section_count || 0),
    selectedIncludedSourceSections.length
  );
  const selectedScopeSections = useMemo(
    () => selectedSectionData ? [selectedSectionData, ...selectedSectionDescendants] : [],
    [selectedSectionData, selectedSectionDescendants]
  );
  const selectedScopeSectionNumbers = useMemo(
    () => new Set(selectedScopeSections.map((section) => Number(section?.number)).filter(Number.isFinite)),
    [selectedScopeSections]
  );
  const selectedScopeSummary = useMemo(() => {
    const sectionsInScope = selectedScopeSections.length;
    const imageCount = selectedScopeSections.reduce(
      (total, section) => total + Number(section?.image_count || 0),
      0
    );
    const imageCandidateCount = selectedScopeSections.reduce(
      (total, section) => total + Number(section?.image_candidates_count ?? section?.image_count ?? 0),
      0
    );
    const tableCount = selectedScopeSections.reduce(
      (total, section) => total + Number(section?.table_count || 0),
      0
    );
    return {
      sectionsInScope,
      imageCount,
      imageCandidateCount,
      tableCount,
    };
  }, [selectedScopeSections]);
  const selectedSectionPreviewPage = useMemo(
    () => selectedSectionData ? getPreviewPage(selectedSectionData) : 1,
    [selectedSectionData]
  );
  const selectedSectionPreviewHeadingTop = useMemo(
    () => selectedSectionData ? getPreviewHeadingTop(selectedSectionData) : 0,
    [selectedSectionData]
  );
  const selectedFeatureTextChunks = useMemo(
    () => (Array.isArray(selectedSectionData?.source_text_chunks)
      ? selectedSectionData.source_text_chunks
      : []),
    [selectedSectionData]
  );
  const selectedFeatureEvidence = useMemo(
    () => selectedFeatureTextChunks
      .map((chunk, index) => ({
        key: `${chunk?.chunk_id || 'evidence'}-${index}`,
        pageLabel: getEvidencePageLabel(chunk),
        excerpt: getEvidenceExcerpt(chunk),
        fullText: getEvidenceFullText(chunk),
        relevance: normalizeEvidenceText(chunk?.relevance),
      }))
      .filter((item) => item.excerpt || item.fullText),
    [selectedFeatureTextChunks]
  );
  const selectedSectionPreviewUrl = useMemo(() => {
    const normalizedPdfId = String(pdfId || '').trim();
    if (!normalizedPdfId || !selectedSectionData) return '';
    const endpoint = `${API_BASE_URL}${API_ENDPOINTS.PDF_SOURCE_FILE(normalizedPdfId)}`;
    const separator = endpoint.includes('?') ? '&' : '?';
    const fragment = buildPdfPreviewFragment(selectedSectionPreviewPage, selectedSectionPreviewHeadingTop);
    return `${endpoint}${separator}t=${Date.now()}#${fragment}`;
  }, [pdfId, selectedSectionData, selectedSectionPreviewHeadingTop, selectedSectionPreviewPage]);
  const normalizedOutlineRows = useMemo(
    () => (Array.isArray(rawSectionOutline) ? rawSectionOutline : [])
      .map(normalizeOutlineRow)
      .filter(Boolean)
      .sort((a, b) => a.row_index - b.row_index),
    [rawSectionOutline]
  );
  const consideredOutlineRowIds = useMemo(
    () => collectConsideredOutlineRowIds(sections, sectionGrouping),
    [sections, sectionGrouping]
  );
  const outlineCoverageByRowId = useMemo(
    () => buildOutlineCoverage(normalizedOutlineRows, consideredOutlineRowIds),
    [normalizedOutlineRows, consideredOutlineRowIds]
  );
  const coveredOutlineRows = useMemo(
    () => normalizedOutlineRows.filter((row) => {
      const coverageInfo = outlineCoverageByRowId.get(row.row_id);
      return !consideredOutlineRowIds.has(row.row_id) && Boolean(coverageInfo?.coveredByChildren);
    }),
    [normalizedOutlineRows, consideredOutlineRowIds, outlineCoverageByRowId]
  );
  const skippedOutlineRows = useMemo(
    () => normalizedOutlineRows.filter((row) => !consideredOutlineRowIds.has(row.row_id)),
    [normalizedOutlineRows, consideredOutlineRowIds]
  );
  const includedOutlineRows = useMemo(
    () => normalizedOutlineRows.filter((row) => consideredOutlineRowIds.has(row.row_id)),
    [normalizedOutlineRows, consideredOutlineRowIds]
  );
  const includedReviewRows = useMemo(
    () => includedOutlineRows,
    [includedOutlineRows]
  );
  const manuallyRestoredRowInfoById = useMemo(() => {
    const output = new Map();
    (sections || []).forEach((section) => {
      if (String(section?.grouping_method || '').trim() !== 'manual_toc_restore') return;
      const rowId = String(section?.source_row_id || '').trim();
      const sectionNumber = Number(section?.number);
      if (!rowId || !Number.isFinite(sectionNumber)) return;
      output.set(rowId, {
        sectionNumber,
        title: String(section?.title || '').trim(),
        hasGenerated: Boolean(sectionArtifactIndex?.[sectionNumber]?.has_generated),
      });
    });
    return output;
  }, [sections, sectionArtifactIndex]);
  const skippedOutlineRowIds = useMemo(
    () => new Set(skippedOutlineRows.map((row) => row.row_id)),
    [skippedOutlineRows]
  );
  const filteredSkippedOutlineRows = useMemo(
    () => filterOutlineRows(skippedOutlineRows, tocSearchQuery),
    [skippedOutlineRows, tocSearchQuery]
  );
  const filteredIncludedOutlineRows = useMemo(
    () => filterOutlineRows(includedReviewRows, tocSearchQuery),
    [includedReviewRows, tocSearchQuery]
  );
  const hasGeneratedSectionArtifacts = useMemo(
    () => Object.values(sectionArtifactIndex || {}).some((artifact) => Boolean(artifact?.has_generated)),
    [sectionArtifactIndex]
  );
  const getTestCaseVersionDetails = (sectionArtifacts = null) => (
    Array.isArray(sectionArtifacts?.test_case_version_details)
      ? sectionArtifacts.test_case_version_details
      : []
  );
  const getBrdMappedTestCaseVersionDetails = (sectionArtifacts = null) => {
    const grouped = new Map();
    getTestCaseVersionDetails(sectionArtifacts).forEach((version) => {
      const brdVersion = Number(version?.brd_version || 0);
      const groupKey = brdVersion > 0 ? `brd-${brdVersion}` : `tc-${version?.version}`;
      const existing = grouped.get(groupKey);
      const versionNumber = Number(version?.version || 0);
      const existingVersionNumber = Number(existing?.version || 0);
      if (!existing || version?.active || (!existing.active && versionNumber >= existingVersionNumber)) {
        grouped.set(groupKey, version);
      }
    });
    return Array.from(grouped.values());
  };
  const getDefaultTestCaseVersion = (sectionArtifacts = null) => {
    const versions = getBrdMappedTestCaseVersionDetails(sectionArtifacts);
    const activeVersion = Number(sectionArtifacts?.active_test_case_version || 0);
    if (activeVersion > 0) return activeVersion;
    const sorted = versions
      .map((version) => Number(version?.version))
      .filter((version) => Number.isFinite(version) && version > 0)
      .sort((a, b) => b - a);
    return sorted[0] || null;
  };
  const isDeprecatedTestCaseArtifact = (sectionArtifacts = null) => (
    Boolean(sectionArtifacts?.test_case_validity?.deprecated || sectionArtifacts?.test_case_validity?.requires_regeneration)
  );
  const openSavedTestCases = (section, sectionArtifacts, version = null) => {
    if (requireTocStructureApproval()) return;
    const sectionNumber = Number(section?.number);
    if (!Number.isFinite(sectionNumber) || sectionNumber <= 0) return;
    const selectedVersion = Number(version || getDefaultTestCaseVersion(sectionArtifacts) || 0);
    onOpenSavedTestCases?.({
      sectionNumber,
      artifactVersion: selectedVersion > 0 ? selectedVersion : null,
    });
  };
  const handleOpenSavedPlan = (event, sectionNumber) => {
    event?.stopPropagation?.();
    if (requireTocStructureApproval()) return;
    const normalizedSection = Number(sectionNumber);
    if (!Number.isFinite(normalizedSection) || normalizedSection <= 0) return;
    onOpenSavedPlan?.(normalizedSection);
  };
  const handleViewSavedTestCases = (event, section, sectionArtifacts) => {
    event.stopPropagation();
    if (requireTocStructureApproval()) return;
    if (isDeprecatedTestCaseArtifact(sectionArtifacts)) {
      setDeprecatedPromptSection({ section, sectionArtifacts });
      return;
    }
    const versions = getBrdMappedTestCaseVersionDetails(sectionArtifacts);
    if (versions.length > 1) {
      setVersionPickerSection({ section, sectionArtifacts });
      return;
    }
    openSavedTestCases(section, sectionArtifacts);
  };
  const brdDeltaReview = useMemo(() => {
    const {
      comparison,
      impact,
      summary,
      displaySummary,
      usesGeneratedSectionSummary,
    } = buildDisplayedSectionChangeSummary(brdChangeAnalysis);
    const changes = Array.isArray(comparison?.changes) ? comparison.changes : [];
    const changeByIdentityKey = new Map();
    const impactByChangeKey = new Map();
    const impactedItems = Array.isArray(impact?.impacted_items) ? impact.impacted_items : [];
    impactedItems.forEach((item) => {
      const key = String(item?.change_key || '').trim();
      if (key) {
        impactByChangeKey.set(key, item);
      }
    });
    const groupedChanges = {
      added: [],
      modified: [],
      removed: [],
    };
    changes.forEach((change) => {
      const changeType = normalizeChangeType(change?.change_type || 'changed') || 'changed';
      if (groupedChanges[changeType]) {
        groupedChanges[changeType].push(change);
      }
      collectChangeIdentityKeys(change).forEach((identityKey) => {
        const current = changeByIdentityKey.get(identityKey) || new Set();
        current.add(changeType);
        changeByIdentityKey.set(identityKey, current);
      });
    });

    const isPending = Boolean(brdUpdatePending || summary?.status === 'pending_extraction');
    const hasAnalysis = Boolean(
      brdChangeAnalysis
      && !isPending
      && (
        comparison?.status
        || changes.length > 0
        || Object.keys(summary || {}).length > 0
      )
    );
    const tocStructureApproved = Boolean(brdChangeAnalysis?.toc_structure_review?.approved);
    return {
      hasAnalysis,
      isPending,
      comparison,
      impact,
      summary,
      displaySummary,
      usesGeneratedSectionSummary,
      changes,
      groupedChanges,
      changeByIdentityKey,
      impactByChangeKey,
      tocStructureApproved,
    };
  }, [brdChangeAnalysis, brdUpdatePending]);

  const readApiError = useCallback(async (response, fallback) => {
    try {
      const payload = await response.json();
      return payload?.detail || payload?.message || fallback;
    } catch (_error) {
      return fallback;
    }
  }, []);

  const tocStructureReviewVersion = Number(brdDeltaReview.summary?.new_version || brdDeltaReview.displaySummary?.new_version || 0);
  const tocStructureReviewKey = `${String(pdfId || '').trim()}:${tocStructureReviewVersion || ''}`;
  const tocStructureComparisonStatus = String(brdDeltaReview.comparison?.status || '').trim().toLowerCase();
  const tocStructureHealthStatus = String(
    brdDeltaReview.comparison?.structure_health?.status || brdDeltaReview.summary?.structure_status || ''
  ).trim().toLowerCase();
  const isTocStructureBlocked = Boolean(
    brdDeltaReview.hasAnalysis
    && (tocStructureComparisonStatus === 'structure_changed' || tocStructureHealthStatus === 'blocked')
  );
  const hasApprovedTocStructure = Boolean(
    brdDeltaReview.tocStructureApproved || tocStructureContext?.structure_review?.approved
  );
  const needsTocStructureApproval = isTocStructureBlocked && !hasApprovedTocStructure;

  const loadTocStructureContext = useCallback(async () => {
    const version = tocStructureReviewVersion;
    const normalizedPdfId = String(pdfId || '').trim();
    if (!normalizedPdfId || !Number.isFinite(version) || version <= 1) {
      setTocStructureError('BRD version context is unavailable for TOC review.');
      return;
    }
    setTocStructureLoading(true);
    setTocStructureError('');
    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.BRD_VERSION_TOC_STRUCTURE(normalizedPdfId, version)}`);
      if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to load TOC structure review.'));
      }
      const data = await response.json();
      setTocStructureContext(data);
      setTocStructureApproved(Boolean(data?.structure_review?.approved));
      setTocStructureModalOpen(true);
    } catch (error) {
      setTocStructureError(error?.message || 'Failed to load TOC structure review.');
    } finally {
      setTocStructureLoading(false);
    }
  }, [pdfId, readApiError, tocStructureReviewVersion]);

  const approveTocStructure = async () => {
    const version = tocStructureReviewVersion;
    const normalizedPdfId = String(pdfId || '').trim();
    if (!tocStructureApproved) {
      setTocStructureError('Confirm that you reviewed both TOCs before continuing.');
      return;
    }
    setTocStructureApplying(true);
    setTocStructureError('');
    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.BRD_VERSION_TOC_STRUCTURE_APPROVE(normalizedPdfId, version)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approved: true,
        }),
      });
      if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to approve TOC structure.'));
      }
      const data = await response.json();
      if (data?.change_analysis) {
        onBrdChangeAnalysisUpdate?.(data.change_analysis);
      }
      setTocStructureApproved(true);
      setTocStructureContext((current) => ({
        ...(current || {}),
        structure_review: data?.structure_review || {},
      }));
      setTocStructureModalOpen(false);
    } catch (error) {
      setTocStructureError(error?.message || 'Failed to approve TOC structure.');
    } finally {
      setTocStructureApplying(false);
    }
  };

  const openTocStructureReview = () => {
    if (tocStructureContext) {
      setTocStructureError('');
      setTocStructureApproved(Boolean(tocStructureContext?.structure_review?.approved));
      setTocStructureModalOpen(true);
      return;
    }
    loadTocStructureContext();
  };

  const requireTocStructureApproval = () => {
    if (!needsTocStructureApproval) return false;
    openTocStructureReview();
    return true;
  };

  useEffect(() => {
    setTocStructureContext(null);
    setTocStructureApproved(false);
    setTocStructureError('');
  }, [tocStructureReviewKey]);

  useEffect(() => {
    if (!needsTocStructureApproval) return;
    if (
      tocStructureAutoOpenKey === tocStructureReviewKey
      || tocStructureModalOpen
      || tocStructureContext
      || tocStructureLoading
    ) {
      return;
    }
    setTocStructureAutoOpenKey(tocStructureReviewKey);
    loadTocStructureContext();
  }, [
    needsTocStructureApproval,
    tocStructureAutoOpenKey,
    tocStructureReviewKey,
    tocStructureModalOpen,
    tocStructureContext,
    tocStructureLoading,
    loadTocStructureContext,
  ]);
  const selectedSkippedTocCount = useMemo(
    () => Array.from(selectedSkippedTocRowIds).filter((rowId) => skippedOutlineRowIds.has(rowId)).length,
    [selectedSkippedTocRowIds, skippedOutlineRowIds]
  );
  const visibleOutlineReviewRows = useMemo(
    () => tocReviewMode === 'included' ? filteredIncludedOutlineRows : filteredSkippedOutlineRows,
    [filteredIncludedOutlineRows, filteredSkippedOutlineRows, tocReviewMode]
  );
  const outlinePreviewRow = useMemo(() => {
    if (visibleOutlineReviewRows.length === 0) return null;
    const normalizedRowId = String(outlinePreviewRowId || '').trim();
    return visibleOutlineReviewRows.find((row) => row.row_id === normalizedRowId) || visibleOutlineReviewRows[0];
  }, [outlinePreviewRowId, visibleOutlineReviewRows]);
  const outlinePreviewPage = useMemo(
    () => outlinePreviewRow ? getPreviewPage(outlinePreviewRow, outlinePreviewRow.page_range) : 1,
    [outlinePreviewRow]
  );
  const outlinePreviewHeadingTop = useMemo(
    () => outlinePreviewRow ? getPreviewHeadingTop(outlinePreviewRow) : 0,
    [outlinePreviewRow]
  );
  const outlinePreviewUrl = useMemo(() => {
    const normalizedPdfId = String(pdfId || '').trim();
    if (!normalizedPdfId || !outlinePreviewRow) return '';
    const endpoint = `${API_BASE_URL}${API_ENDPOINTS.PDF_SOURCE_FILE(normalizedPdfId)}`;
    const separator = endpoint.includes('?') ? '&' : '?';
    const fragment = buildPdfPreviewFragment(outlinePreviewPage, outlinePreviewHeadingTop);
    return `${endpoint}${separator}t=${Date.now()}#${fragment}`;
  }, [outlinePreviewHeadingTop, outlinePreviewPage, outlinePreviewRow, pdfId]);
  useEffect(() => {
    setIsPdfPreviewLoading(Boolean(selectedSectionPreviewUrl));
  }, [selectedSectionPreviewUrl]);
  useEffect(() => {
    if (!isSkippedTocModalOpen) return;
    setIsOutlinePreviewLoading(Boolean(outlinePreviewUrl));
  }, [isSkippedTocModalOpen, outlinePreviewUrl]);

  if (!sections.length) return null;
  const isFeatureList = sections.some((section) => section?.source_type === 'feature');
  const isOpenApiOperationList = Boolean(isOpenApiSource) || sections.some((section) => section?.source_type === 'openapi_operation');
  const itemLabel = isOpenApiOperationList ? 'API Operation' : (isFeatureList ? 'Feature' : 'Section');
  const itemLabelLower = itemLabel.toLowerCase();
  const normalizedExtractionMethod = String(sectionGrouping?.extraction_method || '').trim().toLowerCase();
  const resolvedExtractionMethod = sectionGrouping?.extraction_method || (isOpenApiOperationList ? 'OpenAPI Operation Extraction' : (isFeatureList ? 'Feature List' : ''));
  const extractionMethodLabel = formatExtractionMethodLabel(
    resolvedExtractionMethod,
    sectionGrouping
  );
  const extractionMethodNotice = getExtractionMethodNotice(
    resolvedExtractionMethod,
    sectionGrouping
  );
  const isTocExtraction = [
    'toc',
    'embedded toc',
    'layout toc',
    'rebuilt layout toc',
    'word document outline',
    'word_document_outline',
  ].includes(normalizedExtractionMethod);
  const canReviewSkippedToc = Boolean(
    (onRestoreSkippedTocSections || onApplyRebuiltToc)
    && !isFeatureList
    && isTocExtraction
    && normalizedOutlineRows.length > 0
    && (skippedOutlineRows.length > 0 || coveredOutlineRows.length > 0 || manuallyRestoredRowInfoById.size > 0 || onApplyRebuiltToc)
    && sectionGrouping?.method !== 'feature_list_passthrough'
  );
  const requiresReplacementWarning = Boolean(selectedSection && selectedSectionArtifact?.has_generated);
  const selectedPendingPlan = !isBulkSelectionMode && selectedSection
    ? selectedSectionArtifact?.pending_plan || null
    : null;
  const selectedPendingPlanTime = formatPlanTime(selectedPendingPlan?.created_at);
  const selectedPendingPlanCounts = [
    Number.isFinite(Number(selectedPendingPlan?.suggested?.positive_count))
      ? `${Number(selectedPendingPlan.suggested.positive_count)} positive`
      : '',
    Number.isFinite(Number(selectedPendingPlan?.suggested?.negative_count))
      ? `${Number(selectedPendingPlan.suggested.negative_count)} negative`
      : '',
  ].filter(Boolean).join(' / ');
  const selectedSectionTitle = selectedSectionArtifact?.section_title || selectedSectionData?.title || `Selected ${itemLabel}`;
  const bulkReplacementSections = bulkSelectedSectionData.filter((section) =>
    Boolean(sectionArtifactIndex?.[section?.number]?.has_generated)
  );
  const requiresBulkReplacementWarning = bulkReplacementSections.length > 0;

  const closeReplacementModal = () => {
    if (busy) return;
    setIsReplacementModalOpen(false);
  };

  const closeBulkReplacementModal = () => {
    if (busy) return;
    setIsBulkReplacementModalOpen(false);
  };

  const openSkippedTocModal = () => {
    if (busy) return;
    setSkippedTocError('');
    setRebuiltTocError('');
    setTocReviewMode(skippedOutlineRows.length > 0 ? 'skipped' : 'included');
    setIsSkippedTocModalOpen(true);
  };

  const closeSkippedTocModal = () => {
    if (tocMutationBusy) return;
    setIsSkippedTocModalOpen(false);
    setSkippedTocError('');
    setRebuiltTocError('');
  };

  const toggleSkippedTocRow = (rowId) => {
    const normalized = String(rowId || '').trim();
    if (!normalized || !skippedOutlineRowIds.has(normalized) || tocMutationBusy) return;
    setSelectedSkippedTocRowIds((current) => {
      const next = new Set(current || []);
      if (next.has(normalized)) {
        next.delete(normalized);
      } else {
        next.add(normalized);
      }
      return next;
    });
  };

  const selectVisibleSkippedTocRows = () => {
    if (tocMutationBusy) return;
    setSelectedSkippedTocRowIds((current) => {
      const next = new Set(current || []);
      filteredSkippedOutlineRows.forEach((row) => {
        if (outlineCoverageByRowId.get(row.row_id)?.coveredByChildren) return;
        next.add(row.row_id);
      });
      return next;
    });
  };

  const clearSelectedSkippedTocRows = () => {
    if (tocMutationBusy) return;
    setSelectedSkippedTocRowIds(new Set());
  };

  const restoreSelectedSkippedTocRows = async () => {
    const rowIds = Array.from(selectedSkippedTocRowIds).filter((rowId) => skippedOutlineRowIds.has(rowId));
    if (rowIds.length === 0) {
      setSkippedTocError('Select at least one skipped outline section.');
      return;
    }

    setIsRestoringSkippedToc(true);
    setSkippedTocError('');
    try {
      const previousCount = sections.length;
      const data = await onRestoreSkippedTocSections?.(rowIds);
      const nextSections = Array.isArray(data?.sections) ? data.sections : [];
      const firstRestoredSection = nextSections[previousCount] || null;
      setSelectedSkippedTocRowIds(new Set());
      setTocSearchQuery('');
      setIsSkippedTocModalOpen(false);
      if (!isBulkSelectionMode && firstRestoredSection?.number) {
        onSelectSection?.(firstRestoredSection.number);
      }
    } catch (err) {
      setSkippedTocError(err?.message || 'Failed to restore skipped outline sections.');
    } finally {
      setIsRestoringSkippedToc(false);
    }
  };

  const removeRestoredTocRow = async (rowId) => {
    const normalized = String(rowId || '').trim();
    const restoredInfo = manuallyRestoredRowInfoById.get(normalized);
    if (!normalized || !restoredInfo || tocMutationBusy) return;
    if (restoredInfo.hasGenerated) {
      setSkippedTocError('Generated output exists for this restored section, so it cannot be removed here.');
      return;
    }

    setRemovingRestoredTocRowId(normalized);
    setSkippedTocError('');
    try {
      await onRemoveRestoredTocSections?.([normalized]);
      setSelectedSkippedTocRowIds((current) => {
        const next = new Set(current || []);
        next.delete(normalized);
        return next;
      });
      setTocReviewMode('skipped');
      if (!isBulkSelectionMode) {
        onSelectSection?.(null);
      }
    } catch (err) {
      setSkippedTocError(err?.message || 'Failed to remove restored outline section.');
    } finally {
      setRemovingRestoredTocRowId('');
    }
  };

  const handleRebuildToc = async () => {
    if (!onApplyRebuiltToc || tocMutationBusy) return;
    const confirmed = !hasGeneratedSectionArtifacts || window.confirm(
      'Generated outputs already exist for this BRD. Rebuilding the outline will clear stale generated outputs and replace the section map. Continue?'
    );
    if (!confirmed) return;

    setIsRebuildingToc(true);
    setRebuiltTocError('');
    try {
      await onApplyRebuiltToc({ confirmReplaceExisting: true });
      setSelectedSkippedTocRowIds(new Set());
      setTocSearchQuery('');
      setIsSkippedTocModalOpen(false);
      onSelectSection?.(null);
    } catch (err) {
      setRebuiltTocError(err?.message || 'Failed to rebuild the outline.');
    } finally {
      setIsRebuildingToc(false);
    }
  };

  const handleProcessClick = () => {
    if (requireTocStructureApproval()) return;
    if (requiresReplacementWarning) {
      setIsReplacementModalOpen(true);
      return;
    }

    onProcess?.({ replaceExisting: false });
  };

  const handleBulkGenerateClick = () => {
    if (requireTocStructureApproval()) return;
    if (requiresBulkReplacementWarning) {
      setIsBulkReplacementModalOpen(true);
      return;
    }

    onStartBulkGeneration?.();
  };

  const confirmReplacement = () => {
    if (requireTocStructureApproval()) return;
    setIsReplacementModalOpen(false);
    onProcess?.({ replaceExisting: true });
  };

  const confirmBulkReplacement = () => {
    if (requireTocStructureApproval()) return;
    setIsBulkReplacementModalOpen(false);
    onStartBulkGeneration?.();
  };

  const busyActionLabel = bulkJobMode.startsWith('pre-analysis')
      ? 'Preparing Analysis Plan...'
      : status === 'bulk_processing'
        ? 'Generating Section...'
        : 'Analyzing Section...';
  const bulkCanStart = isBulkSelectionMode && bulkSelectedCount > 0 && !busy;

  const toggleSectionExpansion = (sectionNumber) => {
    const key = String(sectionNumber || '').trim();
    if (!key) return;
    setExpandedSectionNumbers((current) => {
      const next = new Set(current || []);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const selectSectionNode = (section) => {
    if (busy) return;
    if (isBulkSelectionMode) {
      onToggleBulkSection?.(section.number);
    } else {
      if (Number(selectedSection) !== Number(section.number)) {
        setIsPdfPreviewLoading(true);
      }
      onSelectSection?.(section.number);
    }
  };

  const renderReplacementModal = () => {
    if (!isReplacementModalOpen) return null;

    return ReactDOM.createPortal(
      <div className="modal-overlay" role="presentation" onClick={closeReplacementModal}>
        <div
          className="modal-shell max-w-md"
          role="dialog"
          aria-modal="true"
          aria-labelledby="replace-section-warning-title"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h4 id="replace-section-warning-title" className="text-lg font-semibold text-slate-900">
                  Analyze This {itemLabel} Again?
                </h4>
                <p className="mt-1 text-sm text-slate-600">
                  {itemLabel} {selectedSection}: {selectedSectionTitle}
                </p>
              </div>
              <button
                type="button"
                onClick={closeReplacementModal}
                disabled={busy}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Close replace section dialog"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="space-y-3 bg-white px-5 py-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 text-amber-700" />
                <p className="text-sm text-amber-900">
                  Test cases already exist for this {itemLabelLower}. If you proceed, the regenerated test cases
                  will be saved as a new version and the previous version will stay in history.
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Existing generated outputs remain available from the version history.
            </p>
          </div>

          <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4">
            <button
              type="button"
              onClick={closeReplacementModal}
              disabled={busy}
              className="secondary-btn px-4 py-2"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmReplacement}
              disabled={busy}
              className="primary-btn bg-gradient-to-r from-blue-600 to-cyan-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Proceed
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const renderBulkReplacementModal = () => {
    if (!isBulkReplacementModalOpen) return null;
    const replacementCount = bulkReplacementSections.length;
    const replacementLabel = `${replacementCount} ${itemLabelLower}${replacementCount === 1 ? '' : 's'}`;

    return ReactDOM.createPortal(
      <div className="modal-overlay" role="presentation" onClick={closeBulkReplacementModal}>
        <div
          className="modal-shell max-w-lg"
          role="dialog"
          aria-modal="true"
          aria-labelledby="replace-bulk-warning-title"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h4 id="replace-bulk-warning-title" className="text-lg font-semibold text-slate-900">
                  Regenerate Selected {itemLabel}s?
                </h4>
                <p className="mt-1 text-sm text-slate-600">
                  Generated output already exists for {replacementLabel}.
                </p>
              </div>
              <button
                type="button"
                onClick={closeBulkReplacementModal}
                disabled={busy}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Close bulk regeneration dialog"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="space-y-3 bg-white px-5 py-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 text-amber-700" />
                <p className="text-sm text-amber-900">
                  Regeneration after a BRD change will save a new BRD-mapped test-case set. Previous BRD mappings remain available in history.
                </p>
              </div>
            </div>
            <div className="max-h-44 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-2">
              {bulkReplacementSections.map((section) => (
                <div
                  key={section.number}
                  className="rounded-lg bg-white px-3 py-2 text-sm text-slate-700"
                >
                  <span className="font-semibold text-slate-900">
                    {itemLabel} {section.number}
                  </span>
                  {section.title ? ` · ${section.title}` : ''}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4">
            <button
              type="button"
              onClick={closeBulkReplacementModal}
              disabled={busy}
              className="secondary-btn px-4 py-2"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmBulkReplacement}
              disabled={busy}
              className="primary-btn bg-gradient-to-r from-blue-600 to-cyan-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Regenerate
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const renderSkippedTocModal = () => {
    if (!isSkippedTocModalOpen) return null;

    const isIncludedMode = tocReviewMode === 'included';
    const isSkippedMode = !isIncludedMode;
    const visibleRows = visibleOutlineReviewRows;
    const currentOutlinePreviewRowId = String(outlinePreviewRow?.row_id || '').trim();
    const selectOutlinePreviewRow = (row) => {
      const rowId = String(row?.row_id || '').trim();
      if (!rowId) return;
      if (rowId !== currentOutlinePreviewRowId) {
        setIsOutlinePreviewLoading(true);
      }
      setOutlinePreviewRowId(rowId);
    };
    const openOutlinePreview = () => {
      if (outlinePreviewUrl) {
        window.open(outlinePreviewUrl, '_blank', 'noopener,noreferrer');
      } else {
        onViewPdf?.();
      }
    };

    return ReactDOM.createPortal(
      <div className="modal-overlay" role="presentation" onClick={closeSkippedTocModal}>
        <div
          className="modal-shell flex max-h-[90vh] max-w-7xl flex-col"
          role="dialog"
          aria-modal="true"
          aria-labelledby="skipped-toc-sections-title"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h4 id="skipped-toc-sections-title" className="text-lg font-semibold text-slate-900">
                  Review Outline Sections
                </h4>
                <div className="mt-2 flex flex-wrap gap-2 text-xs font-semibold">
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-emerald-700">
                    {skippedOutlineRows.length} skipped
                  </span>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-slate-700">
                    {includedOutlineRows.length} included
                  </span>
                  {coveredOutlineRows.length > 0 && (
                    <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-emerald-700">
                      {coveredOutlineRows.length} parent-only
                    </span>
                  )}
                  <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-blue-700">
                    {selectedSkippedTocCount} selected
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={closeSkippedTocModal}
                disabled={tocMutationBusy}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Close skipped outline sections dialog"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto bg-white px-5 py-4">
            <div className="grid min-h-0 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(360px,0.9fr)]">
              <div className="min-w-0 space-y-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative min-w-0 flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={tocSearchQuery}
                  onChange={(event) => setTocSearchQuery(event.target.value)}
                  placeholder="Search outline sections"
                  className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex rounded-lg border border-slate-200 bg-slate-100 p-1">
                  <button
                    type="button"
                    onClick={() => setTocReviewMode('skipped')}
                    className={`rounded-md px-3 py-2 text-xs font-semibold ${isSkippedMode
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-slate-700 hover:bg-white'
                    }`}
                  >
                    Skipped
                  </button>
                  <button
                    type="button"
                    onClick={() => setTocReviewMode('included')}
                    className={`rounded-md px-3 py-2 text-xs font-semibold ${isIncludedMode
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-slate-700 hover:bg-white'
                    }`}
                  >
                    Included
                  </button>
                </div>
              </div>
            </div>

            {onApplyRebuiltToc && (
              <div className="rounded-xl border border-indigo-100 bg-indigo-50/70 p-3">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Auto-build Outline</p>
                    <div className="mt-1 flex flex-wrap gap-2 text-xs font-semibold">
                      {hasGeneratedSectionArtifacts && (
                        <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-amber-700">
                          Output exists
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleRebuildToc}
                      disabled={tocMutationBusy}
                      className="secondary-btn border-indigo-200 bg-white px-3 py-2 text-xs text-indigo-700 hover:bg-indigo-50"
                    >
                      {isRebuildingToc ? (
                        <Loader className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RefreshCcw className="h-3.5 w-3.5" />
                      )}
                      Rebuild Outline
                    </button>
                  </div>
                </div>

                {rebuiltTocError && (
                  <div className="mt-3 rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm font-medium text-rose-800">
                    {rebuiltTocError}
                  </div>
                )}
              </div>
            )}

            {isSkippedMode ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-xs font-semibold text-slate-600">
                  {filteredSkippedOutlineRows.length} visible skipped section{filteredSkippedOutlineRows.length === 1 ? '' : 's'}
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={selectVisibleSkippedTocRows}
                    disabled={tocMutationBusy || filteredSkippedOutlineRows.length === 0}
                    className="secondary-btn px-3 py-2 text-xs"
                  >
                    Select Visible
                  </button>
                  <button
                    type="button"
                    onClick={clearSelectedSkippedTocRows}
                    disabled={tocMutationBusy || selectedSkippedTocCount === 0}
                    className="secondary-btn px-3 py-2 text-xs"
                  >
                    Clear
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2">
                <p className="text-xs font-semibold text-blue-800">
                  {filteredIncludedOutlineRows.length} included outline section{filteredIncludedOutlineRows.length === 1 ? '' : 's'}
                </p>
              </div>
            )}

            {skippedTocError && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-800">
                {skippedTocError}
              </div>
            )}

            <div className="max-h-[460px] overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-2">
              {visibleRows.length > 0 ? (
                <div className="space-y-2">
                  {visibleRows.map((row) => {
                    const isAlreadyIncluded = consideredOutlineRowIds.has(row.row_id);
                    const isSelected = selectedSkippedTocRowIds.has(row.row_id) && !isAlreadyIncluded;
                    const restoredInfo = manuallyRestoredRowInfoById.get(row.row_id);
                    const coverageInfo = outlineCoverageByRowId.get(row.row_id) || {};
                    const isCoveredByChildren = !isAlreadyIncluded && Boolean(coverageInfo.coveredByChildren);
                    const canSelectRow = isSkippedMode && !isAlreadyIncluded && !tocMutationBusy;
                    const isRemovingThisRow = removingRestoredTocRowId === row.row_id;
                    const isLockedIncludedRow = isAlreadyIncluded && !restoredInfo;
                    const isPreviewed = currentOutlinePreviewRowId === row.row_id;
                    const toggleCurrentRowSelection = () => {
                      if (!canSelectRow) return;
                      toggleSkippedTocRow(row.row_id);
                    };
                    return (
                      <div
                        key={row.row_id}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          selectOutlinePreviewRow(row);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            selectOutlinePreviewRow(row);
                          }
                        }}
                        className={`w-full rounded-lg border p-3 text-left transition ${isSelected
                          ? isCoveredByChildren
                            ? 'border-emerald-200 bg-emerald-50/50'
                            : 'border-blue-200 bg-white hover:border-blue-300 hover:bg-blue-50/20'
                          : isAlreadyIncluded
                            ? restoredInfo
                              ? 'border-blue-200 bg-blue-50/30'
                              : 'border-slate-200 bg-slate-50/80'
                            : isCoveredByChildren
                              ? 'border-emerald-200 bg-emerald-50/50'
                            : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/40'
                        } ${isPreviewed ? 'ring-2 ring-blue-300 ring-offset-1' : ''} cursor-pointer`}
                      >
                        <div className="flex items-start gap-2 sm:gap-3">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              selectOutlinePreviewRow(row);
                              toggleCurrentRowSelection();
                            }}
                            disabled={!canSelectRow}
                            className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition hover:bg-slate-100 disabled:cursor-default disabled:hover:bg-transparent"
                            title={isCoveredByChildren ? 'Adds parent section only' : isLockedIncludedRow ? 'Included section' : undefined}
                            aria-label={`${isSelected ? 'Deselect' : isCoveredByChildren ? 'Select parent only' : 'Select'} ${row.title || row.section_number}`}
                          >
                            <span className={`inline-flex h-5 w-5 items-center justify-center rounded border text-[11px] font-bold ${isSelected
                              ? 'border-blue-600 bg-blue-600 text-white'
                              : restoredInfo
                                ? 'border-blue-200 bg-white text-blue-600'
                                : isCoveredByChildren
                                  ? 'border-emerald-200 bg-white text-emerald-600'
                                : isLockedIncludedRow
                                  ? 'border-slate-300 bg-white text-slate-500'
                                  : 'border-slate-300 bg-white text-slate-500'
                            }`}>
                              {isSelected ? '✓' : restoredInfo ? (
                                <CheckCircle className="h-3.5 w-3.5" />
                              ) : isLockedIncludedRow ? (
                                <Lock className="h-3 w-3" />
                              ) : ''}
                            </span>
                          </button>
                          <div className="min-w-0 flex-1">
                            <div className="mb-1 flex flex-wrap items-center gap-2">
                              <span className="rounded bg-slate-200 px-2 py-1 text-xs font-semibold text-slate-700">
                                {row.section_number || `Row ${row.row_index}`}
                              </span>
                              <span className="rounded bg-slate-200 px-2 py-1 text-xs text-slate-700">
                                Level {row.level || 1}
                              </span>
                              {row.page_range && (
                                <span className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-600">
                                  Pages {row.page_range}
                                </span>
                              )}
                              {row.child_count > 0 && !isAlreadyIncluded && (
                                <span className="rounded-full border border-cyan-200 bg-cyan-50 px-2 py-1 text-[11px] font-semibold text-cyan-700">
                                  {row.child_count} child section{row.child_count === 1 ? '' : 's'}
                                </span>
                              )}
                              {isLockedIncludedRow && (
                                <span className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600">
                                  Included
                                </span>
                              )}
                              {isCoveredByChildren && (
                                <span className="rounded-full border border-emerald-200 bg-white px-2 py-1 text-[11px] font-semibold text-emerald-700">
                                  Children covered
                                </span>
                              )}
                              {isCoveredByChildren && (
                                <span className="rounded-full border border-emerald-200 bg-white px-2 py-1 text-[11px] font-semibold text-emerald-700">
                                  Adds parent only
                                </span>
                              )}
                              {isCoveredByChildren && coverageInfo.coveredDescendantCount > 0 && (
                                <span className="rounded-full border border-emerald-100 bg-white px-2 py-1 text-[11px] font-semibold text-emerald-700">
                                  {coverageInfo.coveredDescendantCount} covered descendant{coverageInfo.coveredDescendantCount === 1 ? '' : 's'}
                                </span>
                              )}
                              {restoredInfo && (
                                <span className="rounded-full border border-blue-200 bg-white px-2 py-1 text-[11px] font-semibold text-blue-700">
                                  Manually added
                                </span>
                              )}
                              {restoredInfo?.hasGenerated && (
                                <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-700">
                                  Output exists
                                </span>
                              )}
                            </div>
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                              <p className={`font-semibold ${isLockedIncludedRow ? 'text-slate-700' : 'text-slate-900'}`}>
                                {row.title || 'Untitled outline section'}
                              </p>
                              {restoredInfo && (
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    removeRestoredTocRow(row.row_id);
                                  }}
                                  disabled={tocMutationBusy || restoredInfo.hasGenerated}
                                  title={restoredInfo.hasGenerated ? 'Generated output exists' : 'Remove manually added section'}
                                  className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-rose-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {isRemovingThisRow ? (
                                    <Loader className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-3.5 w-3.5" />
                                  )}
                                  Remove
                                </button>
                              )}
                            </div>
                            {(row.parent_section_title || row.full_path) && (
                              <p className="mt-1 line-clamp-1 text-xs text-slate-500">
                                {row.parent_section_title
                                  ? `Parent: ${row.parent_section_title}`
                                  : row.full_path}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex min-h-36 items-center justify-center rounded-lg bg-white px-4 py-8 text-center">
                  <p className="text-sm font-medium text-slate-600">
                    {tocSearchQuery
                      ? 'No outline sections match this search.'
                      : isIncludedMode
                        ? 'No included outline sections found.'
                        : 'No skipped outline sections remain.'}
                  </p>
                </div>
              )}
              </div>
              </div>
              <aside className="min-w-0 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                      PDF Preview
                    </p>
                    {outlinePreviewRow ? (
                      <>
                        <p className="mt-1 truncate text-sm font-semibold text-slate-900">
                          {outlinePreviewRow.title || 'Untitled outline section'}
                        </p>
                        <p className="mt-1 text-xs font-semibold text-slate-600">
                          Pages {outlinePreviewRow.page_range || outlinePreviewPage}
                        </p>
                      </>
                    ) : (
                      <p className="mt-1 text-sm font-semibold text-slate-600">
                        No outline section selected.
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={openOutlinePreview}
                    disabled={!outlinePreviewUrl && !onViewPdf}
                    className="secondary-btn shrink-0 px-3 py-2 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Eye className="h-4 w-4" />
                    Open
                  </button>
                </div>

                <div
                  className="relative mt-3 h-[520px] overflow-hidden rounded-xl border border-slate-200 bg-white"
                  aria-busy={isOutlinePreviewLoading}
                >
                  {outlinePreviewUrl ? (
                    <>
                      <iframe
                        key={`${outlinePreviewRow?.row_id || 'outline'}-${outlinePreviewPage}-${outlinePreviewHeadingTop}`}
                        src={outlinePreviewUrl}
                        title={`PDF preview for outline section ${outlinePreviewRow?.section_number || ''}`}
                        className={`h-full w-full bg-white transition-opacity duration-200 ${isOutlinePreviewLoading ? 'opacity-0' : 'opacity-100'
                          }`}
                        style={{ backgroundColor: '#fff', colorScheme: 'light' }}
                        onLoad={() => {
                          setIsOutlinePreviewLoading(false);
                        }}
                      />
                      {isOutlinePreviewLoading && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white text-slate-600">
                          <Loader className="h-6 w-6 animate-spin text-blue-600" />
                          <div className="text-center">
                            <p className="text-sm font-semibold text-slate-800">Loading PDF preview</p>
                            <p className="mt-1 text-xs text-slate-500">
                              Opening page {outlinePreviewPage}
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex h-full items-center justify-center px-4 text-center">
                      <p className="text-sm font-medium text-slate-600">
                        No PDF preview available.
                      </p>
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs font-medium text-slate-500">
              {isIncludedMode
                ? 'Included rows are read-only except manually added sections without output.'
                : `${selectedSkippedTocCount} section${selectedSkippedTocCount === 1 ? '' : 's'} selected to add.`}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeSkippedTocModal}
                disabled={tocMutationBusy}
                className="secondary-btn px-4 py-2"
              >
                {isIncludedMode ? 'Close' : 'Cancel'}
              </button>
              {!isIncludedMode && (
                <button
                  type="button"
                  onClick={restoreSelectedSkippedTocRows}
                  disabled={tocMutationBusy || selectedSkippedTocCount === 0}
                  className="primary-btn bg-gradient-to-r from-blue-600 to-cyan-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isRestoringSkippedToc ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <ListPlus className="h-4 w-4" />
                      Add Selected
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const renderBrdDeltaReviewPanel = () => {
    if (!brdDeltaReview.hasAnalysis) return null;

    const summary = brdDeltaReview.summary || {};
    const displaySummary = brdDeltaReview.displaySummary || summary;
    const totalChanges = Number(displaySummary.total_changes ?? brdDeltaReview.changes.length ?? 0);
    const rawTotalChanges = Number(summary.total_changes ?? brdDeltaReview.changes.length ?? 0);
    const oldVersion = summary.old_version || 'base';
    const newVersion = summary.new_version || 'current';
    const comparisonStatus = String(brdDeltaReview.comparison?.status || '').trim().toLowerCase();
    const unavailableMessage = brdDeltaReview.comparison?.message || '';
    const structureHealth = brdDeltaReview.comparison?.structure_health || {};
    const structureStatus = String(structureHealth?.status || summary?.structure_status || '').trim().toLowerCase();
    const isStructureBlocked = comparisonStatus === 'structure_changed' || structureStatus === 'blocked';
    const isStructureWarning = !isStructureBlocked && structureStatus === 'warning';
    const structureMetrics = structureHealth?.metrics || {};
    const structureMessage = structureHealth?.message || unavailableMessage || 'The BRD TOC structure changed and needs review.';
    const changeRows = brdDeltaReview.changes.map((change, index) => {
      const changeKey = getChangeReviewKey(change, index);
      return {
        change,
        index,
        changeKey,
        changeType: normalizeChangeType(change?.change_type || 'changed'),
      };
    });
    const groupedRows = {
      modified: changeRows.filter((row) => row.changeType === 'modified'),
      added: changeRows.filter((row) => row.changeType === 'added'),
      removed: changeRows.filter((row) => row.changeType === 'removed' || row.changeType === 'deleted'),
    };

    const normalizeDiffTextList = (value) => (
      Array.isArray(value)
        ? value.map((item) => String(item || '').trim()).filter(Boolean)
        : []
    );

    const renderContentDiff = (change) => {
      const contentDiff = change?.diff?.content_diff || change?.content_diff || {};
      const added = normalizeDiffTextList(contentDiff.added);
      const removed = normalizeDiffTextList(contentDiff.removed);
      const modified = Array.isArray(contentDiff.modified)
        ? contentDiff.modified
          .map((item) => ({
            before: String(item?.before || '').trim(),
            after: String(item?.after || '').trim(),
          }))
          .filter((item) => item.before || item.after)
        : [];
      const hasDiffPreview = added.length > 0 || removed.length > 0 || modified.length > 0;

      if (!hasDiffPreview) return null;

      const addedHidden = Math.max(0, Number(contentDiff.added_count || 0) - added.length);
      const removedHidden = Math.max(0, Number(contentDiff.removed_count || 0) - removed.length);
      const modifiedHidden = Math.max(0, Number(contentDiff.modified_count || 0) - modified.length);
      const hiddenCount = addedHidden + removedHidden + modifiedHidden;

      return (
        <div className="mt-3 space-y-2 text-xs">
          {added.length > 0 && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-900">
              <p className="mb-1 font-semibold">Added content</p>
              <ul className="space-y-1">
                {added.map((line, lineIndex) => (
                  <li key={`added-${lineIndex}`} className="leading-5">
                    <span className="mr-1 font-bold text-emerald-700">+</span>
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {removed.length > 0 && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-rose-900">
              <p className="mb-1 font-semibold">Removed content</p>
              <ul className="space-y-1">
                {removed.map((line, lineIndex) => (
                  <li key={`removed-${lineIndex}`} className="leading-5">
                    <span className="mr-1 font-bold text-rose-700">-</span>
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {modified.length > 0 && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-blue-950">
              <p className="mb-2 font-semibold">Changed content</p>
              <div className="space-y-2">
                {modified.map((item, itemIndex) => (
                  <div key={`modified-${itemIndex}`} className="grid gap-2 md:grid-cols-2">
                    {item.before && (
                      <div className="rounded-md bg-white/80 px-2 py-2">
                        <p className="mb-1 font-semibold text-rose-700">Before</p>
                        <p className="leading-5 text-slate-700">{item.before}</p>
                      </div>
                    )}
                    {item.after && (
                      <div className="rounded-md bg-white/80 px-2 py-2">
                        <p className="mb-1 font-semibold text-emerald-700">After</p>
                        <p className="leading-5 text-slate-700">{item.after}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {hiddenCount > 0 && (
            <p className="text-[11px] font-medium text-slate-500">
              Showing the first few content changes; {hiddenCount} more change{hiddenCount === 1 ? '' : 's'} hidden.
            </p>
          )}
        </div>
      );
    };

    const renderChangeRow = (row) => {
      const { change, index, changeKey, changeType } = row;
      const requirement = getChangeRequirementSection(change);
      const impactItem = brdDeltaReview.impactByChangeKey.get(changeKey)
        || brdDeltaReview.impactByChangeKey.get(String(change?.change_key || '').trim())
        || null;
      const generatedSections = getUniqueGeneratedSections(impactItem);
      const oldSection = change?.old_section || {};
      const newSection = change?.new_section || {};
      const diff = change?.diff || {};
      const requirementDelta = change?.requirement_delta || {};
      const requirementDeltaSummary = String(requirementDelta?.summary || '').trim();
      const changedFields = [
        change?.text_changed ? 'Content changed' : '',
        change?.title_changed ? 'Title changed' : '',
        change?.asset_changed ? 'Images/tables changed' : '',
      ].filter(Boolean);

      return (
        <div key={changeKey || `${changeType}-${index}`} className="px-4 py-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <span className="min-w-0">
                <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${getChangeTypeClass(changeType)}`}>
                  {getChangeTypeLabel(changeType)}
                </span>
                <span className="mt-2 block text-sm font-semibold text-slate-900">
                  {generatedSections.length > 0
                    ? generatedSections.map(formatGeneratedSectionLabel).join(', ')
                    : getRequirementLabel(requirement)}
                </span>
                {generatedSections.length > 0 && (
                  <span className="mt-1 block text-xs font-medium text-slate-600">
                    {getSourceChangeLabel(changeType)}: {getRequirementLabel(requirement)}
                  </span>
                )}
                {(requirement?.identity_path || requirement?.path) && (
                  <span className="mt-1 block truncate text-xs text-slate-500">
                    Source path: {requirement.identity_path || requirement.path}
                  </span>
                )}
              </span>
            </div>
          </div>

          {(oldSection?.title || newSection?.title) && oldSection?.title !== newSection?.title && (
            <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
              {oldSection?.title && <p>Old: {getRequirementLabel(oldSection)}</p>}
              {newSection?.title && <p>New: {getRequirementLabel(newSection)}</p>}
            </div>
          )}

          <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-600">
            {changedFields.length > 0
              ? changedFields.map((field) => <span key={field}>{field}</span>)
              : <span>{getChangeTypeLabel(changeType)} section</span>}
            {Number.isFinite(Number(diff.word_delta)) && Number(diff.word_delta) !== 0 && (
              <span>Word delta {Number(diff.word_delta) > 0 ? '+' : ''}{Number(diff.word_delta)}</span>
            )}
            {changeType === 'modified' && (
              <span className="font-semibold text-blue-700">Regeneration will create a new test-case version</span>
            )}
          </div>
          {renderContentDiff(change)}
          {requirementDeltaSummary && (
            <p className="mt-2 text-xs leading-5 text-slate-600">
              {requirementDeltaSummary}
            </p>
          )}
        </div>
      );
    };

    const renderChangeGroup = (title, rows, emptyText) => (
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <h4 className="text-sm font-bold text-slate-900">{title}</h4>
        </div>
        <div className="divide-y divide-slate-100">
          {rows.length > 0 ? rows.map(renderChangeRow) : (
            <p className="px-4 py-4 text-sm text-slate-500">{emptyText}</p>
          )}
        </div>
      </div>
    );

    return (
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-4 md:px-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                BRD Change Review
              </p>
              <h3 className="mt-1 text-xl font-bold text-slate-900">
                Version {oldVersion} to {newVersion} delta analysis
              </h3>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setIsDeltaReviewExpanded((current) => !current)}
                className="secondary-btn px-3 py-2 text-sm"
              >
                {isDeltaReviewExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                {isDeltaReviewExpanded ? 'Hide Details' : 'View Details'}
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-4 p-4 md:p-5">
          {isStructureBlocked ? (
            <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-4 text-sm leading-6 text-amber-900">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
                <div className="min-w-0">
                  <p className="font-bold text-amber-950">TOC structure changed too much to trust this delta.</p>
                  <p className="mt-1">{structureMessage}</p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-3">
                    <div className="rounded-lg border border-amber-200 bg-white/70 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-700">Matched</p>
                      <p className="mt-1 font-bold text-slate-900">
                        {Number(structureMetrics.matched_sections || 0)} / {Math.max(Number(structureMetrics.old_sections_count || 0), Number(structureMetrics.new_sections_count || 0))}
                      </p>
                    </div>
                    <div className="rounded-lg border border-amber-200 bg-white/70 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-700">Old TOC</p>
                      <p className="mt-1 font-bold text-slate-900">{Number(structureMetrics.old_sections_count || 0)} sections</p>
                    </div>
                    <div className="rounded-lg border border-amber-200 bg-white/70 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-700">New TOC</p>
                      <p className="mt-1 font-bold text-slate-900">{Number(structureMetrics.new_sections_count || 0)} sections</p>
                    </div>
                  </div>
                  {Array.isArray(structureHealth?.reasons) && structureHealth.reasons.length > 0 && (
                    <ul className="mt-3 list-disc space-y-1 pl-5 text-xs leading-5">
                      {structureHealth.reasons.map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-4 rounded-xl border border-amber-200 bg-white/80 p-3">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div>
                        <p className="text-sm font-bold text-slate-900">Review old and new TOC</p>
                        <p className="mt-1 text-xs leading-5 text-slate-600">
                          Compare the extracted TOCs side by side and approve the structure before continuing.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={openTocStructureReview}
                        disabled={tocStructureLoading}
                        className="secondary-btn px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {tocStructureLoading ? <Loader className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                        {tocStructureLoading ? 'Loading TOC...' : 'Review TOC'}
                      </button>
                    </div>
                    {tocStructureError && (
                      <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-5 text-rose-700">
                        {tocStructureError}
                      </p>
                    )}
                    {hasApprovedTocStructure && (
                      <p className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
                        TOC structure reviewed and approved.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                {[
                  ['Added', Number(displaySummary.added || 0), 'text-emerald-700'],
                  ['Modified', Number(displaySummary.modified || 0), 'text-blue-700'],
                  ['Removed', Number(displaySummary.removed ?? displaySummary.deleted ?? 0), 'text-rose-700'],
                ].map(([label, value, valueClass]) => (
                  <div key={label} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</p>
                    <p className={`mt-1 text-2xl font-bold ${valueClass}`}>{value}</p>
                  </div>
                ))}
              </div>

              {isStructureWarning && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-5 text-amber-800">
                  {structureMessage}
                </div>
              )}

              {brdDeltaReview.usesGeneratedSectionSummary && rawTotalChanges !== totalChanges && (
                <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm leading-5 text-blue-800">
                  Showing generated-section impact: {rawTotalChanges} source BRD change{rawTotalChanges === 1 ? '' : 's'} roll up to {totalChanges} section{totalChanges === 1 ? '' : 's'} available for regeneration.
                </div>
              )}

              {comparisonStatus === 'unavailable' && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-5 text-amber-800">
                  {unavailableMessage || 'Base BRD snapshot was unavailable, so detailed comparison could not be produced.'}
                </div>
              )}

              {totalChanges === 0 && comparisonStatus !== 'unavailable' ? (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-5 text-emerald-800">
                  No TOC section changes were detected between these BRD versions.
                </div>
              ) : isDeltaReviewExpanded ? (
                <div className="space-y-3">
                  {renderChangeGroup('Modified Sections', groupedRows.modified, 'No modified sections.')}
                  {renderChangeGroup('Added Sections', groupedRows.added, 'No added sections.')}
                  {renderChangeGroup('Removed Sections', groupedRows.removed, 'No removed sections.')}
                </div>
              ) : (
                null
              )}
            </>
          )}
        </div>
      </section>
    );
  };

  const renderSectionNode = (section, depth = 0, parentIdentitySegments = []) => {
    const sectionArtifacts = sectionArtifactIndex?.[section.number];
    const hasGenerated = Boolean(sectionArtifacts?.has_generated);
    const hasTestCases = Number(sectionArtifacts?.test_case_versions || 0) > 0;
    const pendingPlan = sectionArtifacts?.pending_plan || null;
    const brdMappedTestCaseVersions = getBrdMappedTestCaseVersionDetails(sectionArtifacts);
    const testCaseVersionCount = brdMappedTestCaseVersions.length;
    const isDeprecatedTestCases = isDeprecatedTestCaseArtifact(sectionArtifacts);
    const isSelected = isBulkSelectionMode
      ? (Array.isArray(bulkSelectedSections) && bulkSelectedSections.some((value) => Number(value) === Number(section.number)))
      : Number(selectedSection) === Number(section.number);
    const isIncludedInSelectedScope = !isBulkSelectionMode && !isSelected && selectedScopeSectionNumbers.has(Number(section.number));
    const childCount = Array.isArray(section.children) ? section.children.length : 0;
    const hasChildren = childCount > 0;
    const isExpanded = resolvedExpandedSectionNumbers.has(String(section.section_number));
    const includedSourceCount = Math.max(
      Number(section?.included_section_count || 0),
      getIncludedSourceSections(section).length
    );
    const hasGroupedSources = includedSourceCount > 1;
    const sectionDeltaTypes = collectSectionIdentityKeys(section, parentIdentitySegments)
      .flatMap((identityKey) => Array.from(brdDeltaReview.changeByIdentityKey.get(identityKey) || []))
      .filter((changeType) => normalizeChangeType(changeType) === 'modified');
    const isDeltaImpacted = sectionDeltaTypes.length > 0;

    return (
      <div key={section.number} className={depth > 0 ? 'ml-4 border-l border-slate-200 pl-3' : ''}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => selectSectionNode(section)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              selectSectionNode(section);
            }
          }}
          className={`group w-full cursor-pointer rounded-xl border bg-white p-3 text-left transition-all ${isSelected
            ? 'border-blue-500 bg-blue-50 shadow-sm'
            : isIncludedInSelectedScope
              ? 'border-blue-200 bg-blue-50/50'
              : isDeltaImpacted
                ? 'border-slate-300 bg-slate-50 hover:border-blue-300'
                : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
            }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${isSelected
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-700 group-hover:bg-blue-100 group-hover:text-blue-700'
                }`}
            >
              {section.number}
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                {isBulkSelectionMode && (
                  <span
                    className={`inline-flex h-5 w-5 items-center justify-center rounded border text-[11px] font-bold ${isSelected
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-slate-300 bg-white text-slate-500'
                      }`}
                  >
                    {isSelected ? '✓' : ''}
                  </span>
                )}
                <span className="rounded bg-slate-200 px-2 py-1 text-xs text-slate-700">
                  {section.section_number}
                </span>
                <span className="rounded bg-slate-200 px-2 py-1 text-xs text-slate-700">
                  Level {section.level}
                </span>
                {hasChildren && (
                  <span className="rounded-full border border-cyan-200 bg-cyan-50 px-2 py-1 text-[11px] font-semibold text-cyan-700">
                    {childCount} child {itemLabelLower}{childCount === 1 ? '' : 's'}
                  </span>
                )}
                {hasGroupedSources && (
                  <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-1 text-[11px] font-semibold text-violet-700">
                    {includedSourceCount} source sections
                  </span>
                )}
                {isFeatureList && section.feature_priority && (
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700">
                    {section.feature_priority}
                  </span>
                )}
                {hasGenerated && (
                  <span className={`rounded-full border px-2 py-1 text-[11px] font-semibold ${
                    isDeprecatedTestCases
                      ? 'border-amber-200 bg-amber-50 text-amber-800'
                      : 'border-blue-200 bg-blue-50 text-blue-700'
                  }`}>
                    {isDeprecatedTestCases ? 'Deprecated test cases' : 'Generated output available'}
                  </span>
                )}
                {pendingPlan && (
                  <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-1 text-[11px] font-semibold text-violet-700">
                    Plan ready
                  </span>
                )}
                {isDeltaImpacted && (
                  <span className={`rounded-full border px-2 py-1 text-[11px] font-semibold ${getChangeTypeClass('modified')}`}>
                    Updated
                  </span>
                )}
                {isIncludedInSelectedScope && (
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700">
                    Included in parent scope
                  </span>
                )}
              </div>
              <p className="min-w-0 font-semibold text-slate-900">{section.title}</p>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                <span>Pages: {section.indexed_pages}</span>
                <span>Images: {getImageCountLabel(section)}</span>
                {typeof section.table_count === 'number' && <span>Tables: {section.table_count}</span>}
                {isFeatureList && section.feature_category && <span>{section.feature_category}</span>}
              </div>
              {isDeltaImpacted && (
                <p className="mt-2 text-xs font-semibold text-blue-700">
                  {hasTestCases
                    ? 'This section changed. Regeneration will save a new test-case version.'
                    : 'This section changed. Generate test cases from the latest BRD.'}
                </p>
              )}
              {(hasTestCases || pendingPlan) && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {pendingPlan && (
                    <button
                      type="button"
                      onClick={(event) => handleOpenSavedPlan(event, section.number)}
                      disabled={busy}
                      className="inline-flex items-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-xs font-semibold text-violet-700 transition-colors hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <ClipboardList className="h-3.5 w-3.5" />
                      Open Plan
                    </button>
                  )}
                  {hasTestCases && (
                    <button
                      type="button"
                      onClick={(event) => handleViewSavedTestCases(event, section, sectionArtifacts)}
                      className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                        isDeprecatedTestCases
                          ? 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100'
                          : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                      }`}
                    >
                      {isDeprecatedTestCases ? <AlertCircle className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      {isDeprecatedTestCases ? 'Regenerate Required' : 'View Test Cases'}
                    </button>
                  )}
                  {testCaseVersionCount > 1 && (
                    <span className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600">
                      {testCaseVersionCount} BRD versions
                    </span>
                  )}
                </div>
              )}
            </div>
            {isSelected && (
              <CheckCircle className="h-6 w-6 flex-shrink-0 text-blue-600" />
            )}
            {hasChildren && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  toggleSectionExpansion(section.section_number);
                }}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} section ${section.section_number}`}
              >
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            )}
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className="mt-2 space-y-2">
            {section.children.map((child) => renderSectionNode(child, depth + 1, [...parentIdentitySegments, section.title]))}
          </div>
        )}
      </div>
    );
  };

  const renderVersionPickerModal = () => {
    if (!versionPickerSection) return null;
    const { section, sectionArtifacts } = versionPickerSection;
    const versions = getBrdMappedTestCaseVersionDetails(sectionArtifacts)
      .slice()
      .sort((a, b) => Number(b?.brd_version || b?.version || 0) - Number(a?.brd_version || a?.version || 0));
    const activeVersion = Number(sectionArtifacts?.active_test_case_version || 0);

    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/45 px-4 py-6">
        <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                BRD-Mapped Test Cases
              </p>
              <h3 className="mt-1 truncate text-lg font-bold text-slate-900">
                {section?.title || `Section ${section?.number || ''}`}
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setVersionPickerSection(null)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              aria-label="Close version picker"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="max-h-[60vh] overflow-auto p-4">
            <div className="space-y-2">
              {versions.map((version) => {
                const versionNumber = Number(version?.version || 0);
                const isActive = versionNumber === activeVersion;
                return (
                  <button
                    key={versionNumber}
                    type="button"
                    onClick={() => {
                      setVersionPickerSection(null);
                      openSavedTestCases(section, sectionArtifacts, versionNumber);
                    }}
                    className={`flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
                      isActive
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-slate-200 bg-white hover:border-blue-200 hover:bg-blue-50/50'
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-bold text-slate-900">
                        {Number(version?.brd_version || 0) > 0 ? `BRD v${version.brd_version} test cases` : 'Saved test cases'}
                        {isActive ? ' · Active' : ''}
                      </span>
                      <span className="mt-1 block text-xs text-slate-500">
                        {Number(version?.total_count || 0)} cases
                        {Number(version?.positive_count || 0) || Number(version?.negative_count || 0)
                          ? ` · ${Number(version?.positive_count || 0)} positive · ${Number(version?.negative_count || 0)} negative`
                          : ''}
                      </span>
                    </span>
                    <ExternalLink className="h-4 w-4 shrink-0 text-blue-600" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const renderDeprecatedPromptModal = () => {
    if (!deprecatedPromptSection) return null;
    const { section, sectionArtifacts } = deprecatedPromptSection;
    const validity = sectionArtifacts?.test_case_validity || {};
    const sourceVersion = validity.source_brd_version;
    const currentVersion = validity.current_brd_version;

    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/45 px-4 py-6">
        <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-amber-700">
                Regeneration Required
              </p>
              <h3 className="mt-1 text-lg font-bold text-slate-900">
                Test cases are deprecated
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setDeprecatedPromptSection(null)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              aria-label="Close deprecated test cases prompt"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-3 px-5 py-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                <p className="text-sm leading-6 text-amber-900">
                  The BRD requirement changed for this section, so the saved test cases are no longer current.
                  Regenerate this section to create a new valid test-case version.
                </p>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700">
              <p className="font-semibold text-slate-900">
                Section {section?.number}: {section?.title}
              </p>
              {(sourceVersion || currentVersion) && (
                <p className="mt-1 text-xs text-slate-500">
                  Existing test cases: BRD v{sourceVersion || '-'} · Current BRD: v{currentVersion || '-'}
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4">
            <button
              type="button"
              onClick={() => setDeprecatedPromptSection(null)}
              className="secondary-btn px-4 py-2"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                const sectionNumber = Number(section?.number);
                setDeprecatedPromptSection(null);
                if (Number.isFinite(sectionNumber) && sectionNumber > 0) {
                  onRegenerateDeprecatedSection?.(sectionNumber);
                }
              }}
              className="primary-btn bg-gradient-to-r from-blue-600 to-cyan-600"
            >
              Regenerate Test Cases
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const renderTocStructureRows = (rows = []) => (
    <div className="max-h-[55vh] space-y-2 overflow-auto pr-1">
      {rows.length === 0 ? (
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-500">
          No TOC sections available.
        </p>
      ) : rows.map((section, index) => (
        <div key={`${section.section_number || index}-${section.identity_key || section.title || index}`} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
          <p className="text-sm font-semibold text-slate-900">{getRequirementLabel(section)}</p>
          {(section.identity_path || section.path) && (
            <p className="mt-1 truncate text-xs text-slate-500">{section.identity_path || section.path}</p>
          )}
          <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-slate-500">
            {section.indexed_pages && <span>Pages {section.indexed_pages}</span>}
            {Number(section.text_length || 0) > 0 && <span>{Number(section.text_length || 0)} chars</span>}
          </div>
        </div>
      ))}
    </div>
  );

  const renderTocStructureModal = () => {
    if (!tocStructureModalOpen || !tocStructureContext) return null;

    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/45 px-4 py-6">
        <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-amber-700">
                TOC Structure Review
              </p>
              <h3 className="mt-1 text-lg font-bold text-slate-900">
                Compare BRD v{tocStructureContext.old_version || '-'} and v{tocStructureContext.new_version || '-'} TOC
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setTocStructureModalOpen(false)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              aria-label="Close TOC structure review"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
            <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h4 className="text-sm font-bold text-slate-900">Previous BRD TOC</h4>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-600">
                    {(tocStructureContext.old_sections || []).length} sections
                  </span>
                </div>
                {renderTocStructureRows(tocStructureContext.old_sections || [])}
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h4 className="text-sm font-bold text-slate-900">Updated BRD TOC</h4>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-600">
                    {(tocStructureContext.new_sections || []).length} sections
                  </span>
                </div>
                {renderTocStructureRows(tocStructureContext.new_sections || [])}
              </div>
            </div>

            <label className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm leading-6 text-amber-900">
              <input
                type="checkbox"
                checked={tocStructureApproved}
                onChange={(event) => setTocStructureApproved(event.target.checked)}
                className="mt-1"
              />
              <span>I reviewed both TOCs and approve continuing with this extracted structure.</span>
            </label>

            {tocStructureError && (
              <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm leading-5 text-rose-700">
                {tocStructureError}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4">
            <button
              type="button"
              onClick={() => setTocStructureModalOpen(false)}
              className="secondary-btn px-4 py-2"
            >
              Review Later
            </button>
            <button
              type="button"
              onClick={approveTocStructure}
              disabled={tocStructureApplying}
              className="primary-btn px-4 py-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {tocStructureApplying ? <Loader className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
              {tocStructureApplying ? 'Approving...' : 'Approve TOC Structure'}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="space-y-6">
      {renderVersionPickerModal()}
      {renderDeprecatedPromptModal()}
      {renderTocStructureModal()}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start">
        <div className="min-w-0">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
            <h2 className="text-2xl font-bold leading-tight text-slate-900">
              Select a {itemLabel}
            </h2>
            <span className="text-lg font-semibold leading-tight text-slate-500">
              ({sections.length} {itemLabelLower}{sections.length === 1 ? '' : 's'} found)
            </span>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <div className="inline-grid min-h-10 grid-cols-2 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
              <button
                type="button"
                onClick={() => onBulkModeChange?.('single')}
                className={`min-h-8 whitespace-nowrap rounded-lg px-3 text-xs font-semibold transition ${!isBulkSelectionMode
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-slate-700 hover:bg-slate-100'
                  }`}
              >
                Single {itemLabel}
              </button>
              <button
                type="button"
                onClick={() => onBulkModeChange?.('bulk')}
                className={`min-h-8 whitespace-nowrap rounded-lg px-3 text-xs font-semibold transition ${isBulkSelectionMode
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-slate-700 hover:bg-slate-100'
                  }`}
              >
                Multiple {itemLabel}s
              </button>
            </div>
            <div
              className="inline-flex min-h-10 items-center rounded-xl border border-indigo-200 bg-indigo-50 px-3 text-sm font-semibold text-indigo-800"
            >
              {isOpenApiOperationList
                ? 'API Testing Mode'
                : 'BRD Only Mode'}
            </div>
            <div className="inline-flex min-h-10 max-w-full items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 text-[13px] leading-none text-emerald-900">
              <span className="shrink-0 font-medium text-emerald-700">
                {isOpenApiOperationList ? 'Source type' : 'Extraction method'}
              </span>
              <span className="h-4 w-px shrink-0 bg-emerald-200" aria-hidden="true" />
              <span className="truncate font-semibold">{extractionMethodLabel}</span>
            </div>
          </div>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1 lg:justify-self-end">
          {canReviewSkippedToc && (
            <button
              type="button"
              onClick={openSkippedTocModal}
              className={`secondary-btn min-h-10 w-full px-3 py-2 text-sm lg:w-72 ${
                skippedOutlineRows.length > 0 ? 'justify-between' : 'justify-center'
              }`}
              disabled={busy}
            >
              <span className="inline-flex min-w-0 items-center gap-2">
                <ListPlus className="h-4 w-4 shrink-0" />
                <span className="truncate">Review Skipped Outline Sections</span>
              </span>
              {skippedOutlineRows.length > 0 && (
                <span className="ml-2 shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-700">
                  {skippedOutlineRows.length}
                </span>
              )}
            </button>
          )}
          {onDownloadExtractedSectionsJson && (
            <button
              type="button"
              onClick={() => onDownloadExtractedSectionsJson()}
              className="secondary-btn min-h-10 w-full px-3 py-2 text-sm lg:w-72"
              disabled={busy}
            >
              <Download className="h-4 w-4" />
              {isOpenApiOperationList ? 'Download Operation Data' : 'Download Section Data'}
            </button>
          )}
        </div>
      </div>

      {extractionMethodNotice && (
        <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${extractionMethodNotice.toneClass}`}>
          <AlertCircle className={`mt-0.5 h-4 w-4 shrink-0 ${extractionMethodNotice.iconClass}`} />
          <div className="min-w-0">
            <p className="text-sm font-semibold">{extractionMethodNotice.title}</p>
            <p className="mt-1 text-sm leading-5 opacity-90">{extractionMethodNotice.description}</p>
          </div>
        </div>
      )}

      {renderBrdDeltaReviewPanel()}

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
              {isOpenApiOperationList ? 'API Contract' : 'Document Outline'}
            </p>
            <h3 className="mt-1 text-lg font-bold text-slate-900">
              Choose from {sections.length} extracted {itemLabelLower}{sections.length === 1 ? '' : 's'}
            </h3>
          </div>
          {isBulkSelectionMode && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700">
                {bulkSelectedCount} selected
              </span>
              <button
                type="button"
                className="secondary-btn px-3 py-2 text-sm"
                onClick={() => onSelectAllBulkSections?.()}
                disabled={busy}
              >
                Select All
              </button>
              <button
                type="button"
                className="secondary-btn px-3 py-2 text-sm"
                onClick={() => onClearBulkSelection?.()}
                disabled={busy || bulkSelectedCount === 0}
              >
                Clear
              </button>
            </div>
          )}
        </div>

        <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
          <div className="space-y-2 rounded-2xl border border-slate-200 bg-slate-50/70 p-3">
            {sectionTree.map((section) => renderSectionNode(section))}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 xl:sticky xl:top-4 xl:self-start">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
              {isBulkSelectionMode ? 'Selected for Generation' : `Selected ${itemLabel}`}
            </p>
            {isBulkSelectionMode ? (
              <>
                <p className="mt-2 text-2xl font-bold text-slate-900">{bulkSelectedCount}</p>
                <p className="mt-1 text-sm text-slate-600">
                  {itemLabelLower}{bulkSelectedCount === 1 ? '' : 's'} ready for generation
                </p>
                {bulkSelectedSectionData.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {bulkSelectedSectionData.slice(0, 4).map((section) => (
                      <span
                        key={section.number}
                        className="rounded-lg border border-blue-100 bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700"
                      >
                        {itemLabel} {section.number}
                      </span>
                    ))}
                    {bulkSelectedSectionData.length > 4 && (
                      <span className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-600">
                        +{bulkSelectedSectionData.length - 4} more
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="mt-3 text-xs text-slate-500">Select one or more {isOpenApiOperationList ? 'operations from the API contract' : 'sections from the document outline'}.</p>
                )}
                {bulkError && (
                  <p className="mt-3 text-xs font-medium text-rose-700">{userFacingBulkError}</p>
                )}
              </>
            ) : selectedSectionData ? (
              <>
                <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-lg font-bold leading-tight text-slate-900">
                        {itemLabel} {selectedSectionData.number}
                      </h3>
                      <p className="mt-1 line-clamp-1 text-sm font-medium text-slate-700">
                        {selectedSectionData.title}
                      </p>
                    </div>
                    {selectedSectionChildCount > 0 && (
                      <span className="shrink-0 rounded-lg border border-cyan-200 bg-cyan-50 px-2 py-1 text-xs font-bold text-cyan-800">
                        {selectedScopeSummary.sectionsInScope} {itemLabelLower}s
                      </span>
                    )}
                    {selectedIncludedSourceCount > 1 && (
                      <span className="shrink-0 rounded-lg border border-violet-200 bg-violet-50 px-2 py-1 text-xs font-bold text-violet-800">
                        {selectedIncludedSourceCount} source sections
                      </span>
                    )}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-700">
                    <div className="rounded-lg bg-white px-3 py-2">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">Pages</p>
                      <p className="mt-0.5 font-semibold">{selectedSectionData.indexed_pages || 'N/A'}</p>
                    </div>
                    <div className="rounded-lg bg-white px-3 py-2">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">Level</p>
                      <p className="mt-0.5 font-semibold">{selectedSectionData.level || 'N/A'}</p>
                    </div>
                    <div className="rounded-lg bg-white px-3 py-2">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">Images</p>
                      <p className="mt-0.5 font-semibold">{getImageCountLabel(selectedSectionData)}</p>
                    </div>
                    <div className="rounded-lg bg-white px-3 py-2">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">Tables</p>
                      <p className="mt-0.5 font-semibold">{selectedSectionData.table_count ?? 0}</p>
                    </div>
                  </div>
                  {selectedSectionChildCount > 0 && (
                    <div className="mt-3 rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-cyan-700">
                        Analysis Scope
                      </p>
                      <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-xs font-semibold text-cyan-900">
                        <span>
                          Parent + {selectedSectionDescendants.length} child {itemLabelLower}{selectedSectionDescendants.length === 1 ? '' : 's'}
                        </span>
                        <span>
                          {selectedScopeSummary.imageCandidateCount > selectedScopeSummary.imageCount
                            ? `${selectedScopeSummary.imageCount} relevant / ${selectedScopeSummary.imageCandidateCount} images`
                            : `${selectedScopeSummary.imageCount} images`} · {selectedScopeSummary.tableCount} tables
                        </span>
                      </div>
                    </div>
                  )}
                  {selectedSectionData?.grouping_method && selectedIncludedSourceSections.length > 0 && (
                    <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-violet-700">
                        Included Source Sections
                      </p>
                      <div className="mt-2 max-h-44 space-y-1 overflow-y-auto pr-1">
                        {selectedIncludedSourceSections.map((source, index) => (
                          <div
                            key={`${source.section_number || 'source'}-${source.row_index || index}`}
                            className="rounded-md bg-white px-2 py-1.5 text-xs text-slate-700"
                          >
                            <span className="font-semibold text-slate-900">
                              {source.section_number || `Row ${source.row_index || index + 1}`}
                            </span>
                            {source.title ? ` · ${source.title}` : ''}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {selectedPendingPlan && (
                  <p className="mt-3 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-800">
                    Analyzed{selectedPendingPlanTime ? ` ${selectedPendingPlanTime}` : ''}. Test cases have not been generated from this plan yet.
                  </p>
                )}
                {selectedSectionArtifact?.has_generated && (
                  <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                    Generated output already exists
                  </p>
                )}
                {isFeatureList && selectedFeatureEvidence.length > 0 && (
                  <div
                    key={`feature-chunks-${selectedSectionData.number}`}
                    className="mt-4 border-t border-slate-200 pt-4"
                  >
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                        BRD Evidence
                      </p>
                      <p className="mt-1 text-xs text-slate-600">
                        {selectedFeatureEvidence.length} source page{selectedFeatureEvidence.length === 1 ? '' : 's'} linked to this feature
                      </p>
                    </div>
                    <div className="mt-3 max-h-[520px] space-y-3 overflow-y-auto pr-1">
                      {selectedFeatureEvidence.map((item, index) => (
                        <details
                          key={`${selectedSectionData.number}-${item.key}`}
                          open={index === 0}
                          className="group rounded-xl border border-slate-200 bg-slate-50 open:bg-white"
                        >
                          <summary className="cursor-pointer list-none px-3 py-3 [&::-webkit-details-marker]:hidden">
                            <div className="flex items-start justify-between gap-3">
                              <span className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-bold text-blue-700">
                                {item.pageLabel}
                              </span>
                              <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-slate-400 transition group-open:rotate-180" />
                            </div>
                            {item.relevance && (
                              <p className="mt-2 text-xs font-semibold leading-5 text-slate-600">
                                {item.relevance}
                              </p>
                            )}
                            <p className="mt-2 text-sm leading-6 text-slate-800">
                              {item.excerpt}
                            </p>
                          </summary>
                          <div className="border-t border-slate-200 px-3 py-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                              Full Extracted Text
                            </p>
                            <div className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap rounded-lg bg-white px-3 py-2 text-sm leading-6 text-slate-700">
                              {item.fullText || 'No text available for this source page.'}
                            </div>
                          </div>
                        </details>
                      ))}
                    </div>
                  </div>
                )}
                {selectedSectionPreviewUrl && (
                  <div className="mt-4 border-t border-slate-200 pt-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                          PDF Preview
                        </p>
                        <p className="mt-1 text-xs font-semibold text-slate-600">
                          Pages {selectedSectionData.indexed_pages || 'N/A'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => onViewPdf?.()}
                        className="secondary-btn px-3 py-2 text-xs"
                      >
                        <Eye className="h-4 w-4" />
                        Open
                      </button>
                    </div>
                    <div
                      className="relative mt-3 h-[520px] overflow-hidden rounded-xl border border-slate-200 bg-white"
                      aria-busy={isPdfPreviewLoading}
                    >
                      <iframe
                        key={`${selectedSectionData.number}-${selectedSectionData.indexed_pages}-${selectedSectionPreviewPage}-${selectedSectionPreviewHeadingTop}`}
                        src={selectedSectionPreviewUrl}
                        title={`PDF preview for section ${selectedSectionData.number}`}
                        className={`h-full w-full bg-white transition-opacity duration-200 ${isPdfPreviewLoading ? 'opacity-0' : 'opacity-100'
                          }`}
                        style={{ backgroundColor: '#fff', colorScheme: 'light' }}
                        onLoad={() => {
                          setIsPdfPreviewLoading(false);
                        }}
                      />
                      {isPdfPreviewLoading && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white text-slate-600">
                          <Loader className="h-6 w-6 animate-spin text-blue-600" />
                          <div className="text-center">
                            <p className="text-sm font-semibold text-slate-800">Loading PDF preview</p>
                            <p className="mt-1 text-xs text-slate-500">
                              Opening page {selectedSectionPreviewPage}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="mt-2 text-sm text-slate-600">
                No section selected yet.
              </p>
            )}
          </div>
        </div>
      </div>

      {selectedSectionArtifact?.has_generated && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-medium text-amber-900">
              Open the latest generated output to review the current result for this {itemLabelLower}.
            </p>
            <button
              onClick={() => {
                if (requireTocStructureApproval()) return;
                onOpenLatestSaved?.(selectedSection);
              }}
              className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
            >
              <History className="h-4 w-4" />
              Open Latest Output
            </button>
          </div>
        </div>
      )}

      {shouldShowBulkGenerationAction ? (
        <button
          type="button"
          onClick={() => {
            if (requireTocStructureApproval()) return;
            onViewBulkGeneration?.();
          }}
          className="primary-btn w-full py-3"
        >
          <History className="h-5 w-5" />
          {normalizedBulkStatus === 'completed' || normalizedBulkStatus === 'partial_completed'
            ? 'View Generated Outputs'
            : 'View Generation Progress'}
        </button>
      ) : isBulkSelectionMode ? (
        <button
          onClick={handleBulkGenerateClick}
          disabled={!bulkCanStart}
          className="primary-btn w-full py-3"
        >
          {busy ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Starting Generation...
            </>
          ) : (
            <>
              Generate Selected ({bulkSelectedCount})
            </>
          )}
        </button>
      ) : selectedPendingPlan ? (
        <div className="rounded-xl border border-violet-200 bg-violet-50 p-4">
          <p className="text-sm font-semibold text-violet-900">
            A saved analysis plan is ready for this {itemLabelLower}
            {selectedPendingPlanCounts ? ` (${selectedPendingPlanCounts})` : ''}.
          </p>
          <p className="mt-1 text-xs text-violet-800">
            Continue to review it and generate test cases, or analyze again for a fresh plan.
          </p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={(event) => handleOpenSavedPlan(event, selectedSection)}
              disabled={busy}
              className="primary-btn flex-1 py-3"
            >
              <ClipboardList className="h-5 w-5" />
              Continue to Saved Plan
            </button>
            <button
              type="button"
              onClick={handleProcessClick}
              disabled={busy}
              className="secondary-btn py-3"
            >
              {busy ? (
                <>
                  <Loader className="h-5 w-5 animate-spin" />
                  {busyActionLabel}
                </>
              ) : (
                <>
                  <RefreshCcw className="h-5 w-5" />
                  Analyze Again
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={handleProcessClick}
          disabled={!selectedSection || busy}
          className="primary-btn w-full py-3"
        >
          {busy ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              {busyActionLabel}
            </>
          ) : (
            <>
              {selectedSectionArtifact?.has_generated ? (
                <>
                  <RefreshCcw className="h-5 w-5" />
                  Analyze & Plan Regeneration
                </>
              ) : (
                'Analyze & Plan Test Cases'
              )}
            </>
          )}
        </button>
      )}

      {renderReplacementModal()}
      {renderBulkReplacementModal()}
      {renderSkippedTocModal()}
    </div>
  );
};
